import { formatDate, formatDateTime } from "../utils/dateFormatter";
/// <reference types="vite/client" />
import {
    Project, PriceClass, ProjectSettings, Transaction, User, Ticket, SystemSettings,
    Notification, TrafficLog, SystemAlert, LiveVisitor, Broadcast, AdminStats, Coupon,
    MarketingCampaign, ConversionSettings, ActivityLog, UserSession, ImpersonationLog,
    BalanceAdjustmentLog, EmailLog, UserNotificationPrefs, UserReferral, AdminUserDetails,
    UserStats, FraudAlert, ProxyProvider, ProxyProviderConfig, ProxySession, GeoLocation,
    ProxyUsageStats, ProxyLog, ProxyLogStats, PaginatedUsersResponse, PaginatedTransactionsResponse,
    RoadmapItem, RoadmapItemAdmin, ChangelogItem, AdminChatSummary, AdminChatAnalytics, NotificationHealth,
    AdminLiveTrafficSnapshot, CreditUsageResponse, ProjectTrafficHistoryResponse,
    LegacySubscription
} from '../types';
import { brandConfig } from '../config/brand';
import { normalizeProjectUrlSettings } from '../utils/urlLists';
import { buildSsoHandoffUrl, getDefaultAuthPath, getSafeSsoTargetOrigin } from '../utils/authRedirect';
import { addBreadcrumb, captureMessage } from './sentry';
import type { ProjectAnalyticsResponse } from '../types/analytics';

export const API_BASE_URL = brandConfig.apiUrl;
const SSO_TARGET_DOMAIN = import.meta.env.VITE_SSO_TARGET_DOMAIN || '';
const LOCAL_PREVIEW_PROJECTS_KEY = 'tc_local_preview_projects';

const isLocalPreviewEnabled = (): boolean => {
    if (!import.meta.env.DEV || typeof window === 'undefined') return false;
    return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

const getActiveUserId = (): string | null => {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('modus_current_user_id');
};

const getStoredLocalPreviewProjects = (): Project[] => {
    if (!isLocalPreviewEnabled()) return [];
    try {
        const data = localStorage.getItem(LOCAL_PREVIEW_PROJECTS_KEY);
        return data ? JSON.parse(data) : [];
    } catch {
        return [];
    }
};

const getLocalPreviewProjects = (): Project[] => {
    const activeUserId = getActiveUserId();
    if (!activeUserId) return [];
    return getStoredLocalPreviewProjects().filter((project) => project.userId === activeUserId);
};

const saveLocalPreviewProjects = (projects: Project[]): void => {
    if (!isLocalPreviewEnabled()) return;
    const activeUserId = getActiveUserId();
    if (!activeUserId) {
        throw new Error('An authenticated user is required to save a campaign preview.');
    }
    const otherUsersProjects = getStoredLocalPreviewProjects().filter((project) => project.userId !== activeUserId);
    const ownedProjects = projects.map((project) => ({ ...project, userId: activeUserId }));
    localStorage.setItem(LOCAL_PREVIEW_PROJECTS_KEY, JSON.stringify([...ownedProjects, ...otherUsersProjects]));
};

const getLocalPreviewProject = (id: string): Project | undefined => (
    getLocalPreviewProjects().find((project) => project.id === id)
);

const isSsoEnabled = (): boolean => {
    if (!SSO_TARGET_DOMAIN || typeof window === 'undefined') return false;
    const ssoOrigin = getSafeSsoTargetOrigin(SSO_TARGET_DOMAIN);
    return Boolean(ssoOrigin && window.location.origin !== ssoOrigin);
};

const getStorageItem = (key: string) => {
    if (typeof localStorage !== 'undefined') {
        return localStorage.getItem(key);
    }
    return null;
};

let authToken = getStorageItem('tgp_token');
let currentUserId = getStorageItem('modus_current_user_id');

const clearStoredAuthSession = () => {
    authToken = null;
    currentUserId = null;
    localStorage.removeItem('tgp_token');
    localStorage.removeItem('modus_current_user');
    localStorage.removeItem('modus_current_user_id');
};

const getApiPath = (url: string): string => {
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        return url;
    }
};

export interface PhoneVerificationPending {
    status: 'requires_phone_verification';
    pendingRegistrationId: string;
    email: string;
    phone: string;
    verificationChannel: string;
    message: string;
    /** 'self_hosted' = our own WhatsApp/email OTP, 'twilio' = Twilio Verify.
     *  Decides which verify/resend endpoint to call. */
    verificationProvider?: 'self_hosted' | 'twilio';
    /** Only a WhatsApp code proves the phone number, so only that path earns the
     *  free signup credits. Shown to the user so the difference is not a surprise. */
    grantsCredits?: boolean;
}

export interface PhoneVerificationComplete {
    status: 'verified';
    email: string;
    message: string;
}

const formatApiErrorDetail = (value: unknown): string | null => {
    if (typeof value === 'string') {
        const message = value.trim().replace(/^Value error,\s*/i, '');
        return message || null;
    }
    if (Array.isArray(value)) {
        const messages = value
            .map(formatApiErrorDetail)
            .filter((message): message is string => Boolean(message));
        return messages.length > 0 ? Array.from(new Set(messages)).join(' ') : null;
    }
    if (value && typeof value === 'object') {
        const detail = value as Record<string, unknown>;
        for (const key of ['message', 'msg', 'detail', 'error']) {
            const message = formatApiErrorDetail(detail[key]);
            if (message) return message;
        }
    }
    return null;
};

const readApiError = async (response: Response, fallback: string): Promise<string> => {
    const contentType = response.headers.get('content-type') || '';
    try {
        if (contentType.includes('application/json')) {
            const body = await response.json();
            const detail = body?.detail || body?.message || body?.error;
            const message = formatApiErrorDetail(detail);
            if (message) return message;
        } else if (contentType.includes('text/plain')) {
            const text = await response.text();
            if (text.trim()) return text.trim().slice(0, 300);
        }
    } catch {
        // Keep the fallback if the response body is missing or malformed.
    }
    return fallback;
};

const recordApiResponse = (url: string, method: string, response: Response) => {
    const requestId = response.headers?.get?.('X-Request-ID') || null;
    if (requestId) {
        try {
            sessionStorage.setItem('last_request_id', requestId);
        } catch {
            // sessionStorage may be unavailable.
        }
    }

    const path = getApiPath(url);
    addBreadcrumb({
        category: 'api',
        message: `${method} ${path} ${response.status}`,
        level: response.ok ? 'info' : response.status >= 500 ? 'error' : 'warning',
        data: {
            method,
            path,
            status: response.status,
            request_id: requestId || undefined,
        },
    });

    if (response.status >= 500 && path === '/auth/login') {
        captureMessage('Login API server error', 'error', {
            api: {
                method,
                path,
                status: response.status,
                request_id: requestId || undefined,
            },
        });
    }
};

const fetchWithAuth = async (url: string, options: any = {}) => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    const token = authToken || getStorageItem('tgp_token');
    if (token) {
        authToken = token;
        headers['Authorization'] = `Bearer ${token}`;
    }

    const method = (options.method || 'GET').toUpperCase();
    const response = await fetch(url, { ...options, headers });
    recordApiResponse(url, method, response);
    if (response.status === 401) {
        clearStoredAuthSession();
        window.dispatchEvent(new Event('auth-expired'));
    }
    return response;
};

const fetchObserved = async (url: string, options: RequestInit = {}) => {
    const method = (options.method || 'GET').toUpperCase();
    const response = await fetch(url, options);
    recordApiResponse(url, method, response);
    return response;
};

const fillMissingDays = (stats: { date: string; visitors: number; pageviews: number }[], days: number = 30): { date: string; visitors: number; pageviews: number }[] => {
    const result: { date: string; visitors: number; pageviews: number }[] = [];
    const statsMap = new Map(stats.map(s => [s.date, s]));

    // Use pure UTC arithmetic to match backend's UTC dates.
    // The old code mixed local-time setDate() with UTC toISOString(),
    // causing date keys to shift ±1 day for non-UTC timezones.
    const now = new Date();
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(todayUTC - i * 86400000);
        const dateStr = d.toISOString().split('T')[0];
        const existing = statsMap.get(dateStr);
        result.push(existing || { date: dateStr, visitors: 0, pageviews: 0 });
    }
    return result;
};

const projectPlanLabel = (project: any): string => {
    const billingTier = String(project?.tier || '').trim().toLowerCase();
    if (billingTier === 'economy' || billingTier === 'professional' || billingTier === 'expert') {
        return billingTier.charAt(0).toUpperCase() + billingTier.slice(1);
    }
    return project?.plan_type || 'Custom';
};

const mapProjectResponse = (p: any, stats: { date: string; visitors: number; pageviews: number }[] = []): Project => ({
    id: p.id,
    userId: p.user_id,
    name: p.name,
    // The customer-facing plan must match the wallet that delivery charges.
    // Legacy rows can retain a stale plan_type after their billing tier changes.
    plan: projectPlanLabel(p),
    tier: p.tier,
    status: p.status,
    startAt: p.start_at,
    expires: p.expires_at || '',
    expiresAt: p.expires_at,
    createdAt: p.created_at,
    settings: normalizeProjectUrlSettings(p.settings),
    stats: fillMissingDays(stats, 30),
    totalHits: p.total_hits || 0,
    hitsToday: p.hits_today || 0,
    dailyLimit: p.daily_limit || 0,
    totalTarget: p.total_target || 0,
    daily_limit: p.daily_limit || 0,
    total_target: p.total_target || 0,
    customTarget: {
        totalVisitors: p.total_target || 0,
        durationDays: p.duration_days || 0,
        dailyLimit: p.daily_limit || 0
    },
    priority: p.priority || 0,
    forceStopReason: p.force_stop_reason,
    isHidden: p.is_hidden || false,
    internalTags: p.internal_tags || [],
    notes: p.notes,
    isFlagged: p.is_flagged || false,
    settingsVersion: p.settings_version || 1,
    deductCredits: p.deduct_credits !== false,
    gscStats: p.gsc_stats ? {
        searchesAttempted: p.gsc_stats.searches_attempted || 0,
        targetsFound: p.gsc_stats.targets_found || 0,
        clicksDelivered: p.gsc_stats.clicks_delivered || p.total_hits || 0,
        failedSearches: p.gsc_stats.failed_searches || 0,
        averageDiscoveryPosition: p.gsc_stats.average_discovery_position ?? null,
        impressions: p.gsc_stats.impressions || 0,
        searchConsoleClicks: p.gsc_stats.search_console_clicks || 0,
        searchConsoleCtr: p.gsc_stats.search_console_ctr || 0,
        searchConsolePosition: p.gsc_stats.search_console_position ?? null,
    } : undefined,
    userEmail: p.user_email,
    userName: p.user_name,
    userBalance: p.user_balance,
});

export interface PublicChatAttachment {
    id?: string;
    url: string;
    filename?: string;
    mime_type?: string;
    kind?: 'image' | 'audio' | 'file';
}

export interface PublicChatStartPayload {
    name: string;
    email: string;
    phone?: string;
    message: string;
    page_url: string;
    referrer?: string;
    utm?: Record<string, string>;
    visitor_id?: string;
    device?: string;
    browser?: string;
    attachments?: PublicChatAttachment[];
    honeypot?: string;
}

export interface FreeTrafficClaimPayload {
    email: string;
    whatsapp_number: string;
    website_url: string;
    traffic_location: string;
    page_url?: string;
    honeypot?: string;
}

export interface FreeTrafficClaimResponse {
    id: string;
    email: string;
    normalized_domain: string;
    traffic_location: string;
    status: string;
    message: string;
    verification_required?: boolean;
    lead_id?: string | null;
    verification_channel?: string | null;
    masked_whatsapp?: string | null;
    debug_verification_code?: string | null;
}

export interface FreeTrafficVerificationResponse {
    status: string;
    message: string;
    email: string;
    user_id: string;
    project_id: string;
    access_token: string;
    token_type: string;
    temporary_password?: string | null;
}

export interface FreeTrafficWebsiteCheckItem {
    key: string;
    label: string;
    passed: boolean;
    message: string;
}

export interface FreeTrafficWebsiteCheckResponse {
    website_url: string;
    normalized_domain: string;
    eligible: boolean;
    analytics_provider?: string | null;
    analytics_id?: string | null;
    checks: FreeTrafficWebsiteCheckItem[];
}

const mapPublicChatResponse = (data: any): Ticket => ({
    id: data.ticket_id,
    supportNumber: data.support_number,
    supportCode: data.support_code,
    type: 'chat',
    userId: data.user_id,
    userName: data.guest_name || data.guest_email || 'Website visitor',
    userEmail: data.guest_email,
    guestEmail: data.guest_email,
    guestPhone: data.guest_phone,
    subject: data.subject || 'Website chat',
    status: data.status || 'open',
    priority: data.priority || 'medium',
    category: 'sales',
    source: data.source || 'user',
    attachmentUrls: data.attachment_urls || [],
    date: data.created_at?.split('T')[0] || formatDate(new Date()),
    lastMessage: data.messages?.length > 0 ? data.messages[data.messages.length - 1].text : 'Chat started',
    messages: data.messages || [],
    unread: !!data.unread,
    updatedAt: data.updated_at,
    lastCustomerMessageAt: data.last_customer_message_at,
    lastAdminReplyAt: data.last_admin_reply_at,
    waitingForAdmin: data.waiting_for_admin,
    csatRating: data.csat_rating,
});

export interface GoogleAuthConfig {
    enabled: boolean;
    clientId: string | null;
}

interface AuthResponse {
    access_token: string;
    user: any;
}

const mapAuthUserResponse = (userData: any): User => ({
    id: userData.id,
    email: userData.email,
    name: userData.email?.split('@')[0] || userData.name || 'User',
    role: userData.role,
    balance: userData.balance,
    balanceEconomy: userData.balance_economy,
    balanceProfessional: userData.balance_professional,
    balanceExpert: userData.balance_expert,
    balanceSeo: userData.balance_seo,
    status: 'active',
    joinedDate: userData.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
    createdAt: userData.created_at,
    projectsCount: userData.projects_count || 0,
    apiKey: userData.api_key,
    isVerified: userData.is_verified ?? false,
    isIdentityVerified: userData.is_identity_verified ?? userData.is_verified ?? false,
    phone: userData.phone,
    phoneVerified: userData.phone_verified,
    phoneVerifiedAt: userData.phone_verified_at,
    phoneVerificationProvider: userData.phone_verification_provider,
    phoneVerificationChannel: userData.phone_verification_channel,
    company: userData.company,
    vatId: userData.vat_id,
    address: userData.address,
    city: userData.city,
    country: userData.country,
    zip: userData.zip,
    website: userData.website,
    displayName: userData.display_name,
    bio: userData.bio,
    jobTitle: userData.job_title,
    publicProfile: userData.public_profile,
    twoFactorEnabled: userData.two_factor_enabled,
    emailFrequency: userData.email_frequency,
    loginNotificationEnabled: userData.login_notification_enabled,
    newsletterSub: userData.newsletter_sub,
    soundEffects: userData.sound_effects,
    developerMode: userData.developer_mode,
    apiWhitelist: userData.api_whitelist,
    webhookSecret: userData.webhook_secret,
    accessibility: userData.accessibility,
    socialLinks: userData.social_links,
    loginHistory: userData.login_history,
    recoveryEmail: userData.recovery_email,
    timezone: userData.timezone,
    language: userData.language,
    themeAccentColor: userData.theme_accent_color,
    skillsBadges: userData.skills_badges,
    referralCode: userData.referral_code,
    supportPin: userData.support_pin,
    dateFormat: userData.date_format,
    numberFormat: userData.number_format,
    requirePasswordReset: userData.require_password_reset,
    gamificationXp: userData.gamification_xp,
    gamificationLevel: userData.gamification_level,
    gamificationTotalSpent: userData.gamification_total_spent,
    gamificationPermanentDiscount: userData.gamification_permanent_discount,
    streakDays: userData.streak_days,
    streakBest: userData.streak_best,
    paypalSubscriptionId: userData.paypal_subscription_id,
});

const persistAuthResponse = (data: AuthResponse, requestedReturnPath?: string): User => {
    authToken = data.access_token;
    localStorage.setItem('tgp_token', data.access_token);

    const mappedUser = mapAuthUserResponse(data.user);
    db.setCurrentUser(mappedUser);

    if (isSsoEnabled()) {
        const ssoUrl = buildSsoHandoffUrl(
            SSO_TARGET_DOMAIN,
            data.access_token,
            requestedReturnPath || getDefaultAuthPath(mappedUser.role),
            mappedUser.role,
        );
        if (ssoUrl) window.location.assign(ssoUrl);
    }

    return mappedUser;
};

export const db = {
    init: () => {
        // No-op for API-based DB, but we keep the signature
        console.log("Remote Database Initialized");
    },

    getCurrentUser: (): User | undefined => {
        try {
            const userData = localStorage.getItem('modus_current_user');
            return userData ? JSON.parse(userData) : undefined;
        } catch (e) {
            console.error('Failed to parse user data from localStorage:', e);
            localStorage.removeItem('modus_current_user');
            return undefined;
        }
    },

    refreshCurrentUser: async (): Promise<User | undefined> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/users/me`);
            if (!response.ok) {
                if (response.status === 401) {
                    db.logout();
                    return undefined;
                }
                throw new Error('Failed to fetch user');
            }
            const userData = await response.json();

            const mappedUser: User = {
                id: userData.id,
                email: userData.email,
                name: userData.name || userData.email?.split('@')[0] || 'User',
                role: userData.role,
                balance: userData.balance,
                balanceEconomy: userData.balance_economy,
                balanceProfessional: userData.balance_professional,
                balanceExpert: userData.balance_expert,
                balanceSeo: userData.balance_seo,
                status: userData.status || 'active',
                plan: userData.plan,
                joinedDate: userData.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
                createdAt: userData.created_at,
                projectsCount: userData.projects_count || 0,
                apiKey: userData.api_key,
                isVerified: userData.is_verified ?? false,
                isIdentityVerified: userData.is_identity_verified ?? userData.is_verified ?? false,
                shadowBanned: userData.shadow_banned,
                notes: userData.notes,
                tags: userData.tags,
                banReason: userData.ban_reason,
                lastIp: userData.last_ip,
                lastActive: userData.last_active,
                phone: userData.phone,
                phoneVerified: userData.phone_verified,
                phoneVerifiedAt: userData.phone_verified_at,
                phoneVerificationProvider: userData.phone_verification_provider,
                phoneVerificationChannel: userData.phone_verification_channel,
                company: userData.company,
                vatId: userData.vat_id,
                address: userData.address,
                city: userData.city,
                country: userData.country,
                zip: userData.zip,
                website: userData.website,
                displayName: userData.display_name,
                bio: userData.bio,
                jobTitle: userData.job_title,
                publicProfile: userData.public_profile,
                twoFactorEnabled: userData.two_factor_enabled,
                emailFrequency: userData.email_frequency,
                loginNotificationEnabled: userData.login_notification_enabled,
                newsletterSub: userData.newsletter_sub,
                soundEffects: userData.sound_effects,
                developerMode: userData.developer_mode,
                apiWhitelist: userData.api_whitelist,
                webhookSecret: userData.webhook_secret,
                accessibility: userData.accessibility,
                socialLinks: userData.social_links,
                loginHistory: userData.login_history,
                recoveryEmail: userData.recovery_email,
                timezone: userData.timezone,
                language: userData.language,
                themeAccentColor: userData.theme_accent_color,
                skillsBadges: userData.skills_badges,
                referralCode: userData.referral_code,
                supportPin: userData.support_pin,
                dateFormat: userData.date_format,
                numberFormat: userData.number_format,
                requirePasswordReset: userData.require_password_reset,
                avatarUrl: userData.avatar_url,
                gamificationXp: userData.gamification_xp,
                gamificationLevel: userData.gamification_level,
                gamificationTotalSpent: userData.gamification_total_spent,
                gamificationPermanentDiscount: userData.gamification_permanent_discount,
                streakDays: userData.streak_days,
                streakBest: userData.streak_best,
                paypalSubscriptionId: userData.paypal_subscription_id,
            };

            db.setCurrentUser(mappedUser);
            return mappedUser;
        } catch (e) {
            console.error('Failed to refresh user:', e);
            return undefined;
        }
    },

    submitFreeTrafficClaim: async (payload: FreeTrafficClaimPayload): Promise<FreeTrafficClaimResponse> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/free-traffic-claim`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to submit free traffic request'));
        }
        return await response.json();
    },

    checkFreeTrafficWebsite: async (website_url: string): Promise<FreeTrafficWebsiteCheckResponse> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/free-traffic-check`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ website_url }),
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to check website eligibility'));
        }
        return await response.json();
    },

    verifyFreeTrafficClaim: async (lead_id: string, code: string): Promise<FreeTrafficVerificationResponse> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/free-traffic-verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lead_id, code }),
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to verify WhatsApp code'));
        }

        const result: FreeTrafficVerificationResponse = await response.json();
        if (result.access_token) {
            authToken = result.access_token;
            localStorage.setItem('tgp_token', result.access_token);
            await db.refreshCurrentUser();
        }
        return result;
    },

    setCurrentUser: (user: User | null) => {
        if (user) {
            localStorage.setItem('modus_current_user', JSON.stringify(user));
            localStorage.setItem('modus_current_user_id', user.id);
            currentUserId = user.id;
        } else {
            localStorage.removeItem('modus_current_user');
            localStorage.removeItem('modus_current_user_id');
            currentUserId = null;
        }
    },

    logout: () => {
        clearStoredAuthSession();
        window.location.href = '/login';
    },

    getGoogleAuthConfig: async (): Promise<GoogleAuthConfig> => {
        const response = await fetchObserved(`${API_BASE_URL}/auth/google/config`);
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to load Google sign-in configuration'));
        }

        const data = await response.json();
        return {
            enabled: data.enabled === true,
            clientId: typeof data.client_id === 'string' && data.client_id.trim()
                ? data.client_id
                : null,
        };
    },

    loginWithGoogle: async (
        credential: string,
        rememberMe: boolean = false,
        requestedReturnPath?: string,
    ): Promise<User> => {
        const response = await fetchObserved(`${API_BASE_URL}/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ credential, remember_me: rememberMe }),
        });

        if (!response.ok) {
            const fallback = response.status >= 500
                ? 'Google sign-in is temporarily unavailable. Please try again in a moment.'
                : 'Google sign-in failed';
            throw new Error(await readApiError(response, fallback));
        }

        return persistAuthResponse(await response.json(), requestedReturnPath);
    },

    signUpWithGoogle: async (
        credential: string,
        refCode?: string,
        requestedReturnPath?: string,
    ): Promise<User> => {
        const gclid = localStorage.getItem('tgp_gclid') || undefined;
        const response = await fetchObserved(`${API_BASE_URL}/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                credential,
                flow: 'signup',
                remember_me: false,
                ref_code: refCode,
                brand_origin: brandConfig.id,
                gclid,
            }),
        });

        if (!response.ok) {
            const fallback = response.status >= 500
                ? 'Google sign-up is temporarily unavailable. Please try again in a moment.'
                : 'Google sign-up failed';
            throw new Error(await readApiError(response, fallback));
        }

        const user = persistAuthResponse(await response.json(), requestedReturnPath);
        localStorage.removeItem('tgp_ref_code');
        if (gclid) localStorage.removeItem('tgp_gclid');
        return user;
    },

    // Auth — single round-trip login (⚡ mobile-optimised)
    login: async (email: string, pass: string, rememberMe: boolean = false, requestedReturnPath?: string) => {
        // Combined endpoint returns token + user data in ONE response.
        // Saves a full network round trip vs the old /auth/token + /users/me flow.
        const response = await fetchObserved(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password: pass, remember_me: rememberMe })
        });

        if (!response.ok) {
            const fallback = response.status >= 500
                ? 'Login is temporarily unavailable. Please try again in a moment.'
                : 'Invalid email or password';
            throw new Error(await readApiError(response, fallback));
        }

        return persistAuthResponse(await response.json(), requestedReturnPath);
    },

    validateSsoToken: async (token: string): Promise<{ valid: boolean; user?: User; token?: string }> => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/validate-sso`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token })
            });

            if (!response.ok) {
                return { valid: false };
            }

            const data = await response.json();
            if (!data.valid || !data.user) {
                return { valid: false };
            }

            const userData = data.user;
            const mappedUser: User = {
                id: userData.id,
                email: userData.email,
                name: userData.email?.split('@')[0] || userData.name || 'User',
                role: userData.role,
                balance: userData.balance,
                balanceEconomy: userData.balance_economy,
                balanceProfessional: userData.balance_professional,
                balanceExpert: userData.balance_expert,
                balanceSeo: userData.balance_seo,
                status: 'active',
                joinedDate: userData.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
                createdAt: userData.created_at,
                projectsCount: userData.projects_count || 0,
                apiKey: userData.api_key,
                isVerified: userData.is_verified ?? false,
                isIdentityVerified: userData.is_identity_verified ?? userData.is_verified ?? false,
                phone: userData.phone,
                phoneVerified: userData.phone_verified,
                phoneVerifiedAt: userData.phone_verified_at,
                phoneVerificationProvider: userData.phone_verification_provider,
                phoneVerificationChannel: userData.phone_verification_channel,
                company: userData.company,
                vatId: userData.vat_id,
                address: userData.address,
                city: userData.city,
                country: userData.country,
                zip: userData.zip,
                website: userData.website,
                displayName: userData.display_name,
                bio: userData.bio,
                jobTitle: userData.job_title,
                publicProfile: userData.public_profile,
                twoFactorEnabled: userData.two_factor_enabled,
                emailFrequency: userData.email_frequency,
                loginNotificationEnabled: userData.login_notification_enabled,
                newsletterSub: userData.newsletter_sub,
                soundEffects: userData.sound_effects,
                developerMode: userData.developer_mode,
                apiWhitelist: userData.api_whitelist,
                webhookSecret: userData.webhook_secret,
                accessibility: userData.accessibility,
                socialLinks: userData.social_links,
                loginHistory: userData.login_history,
                recoveryEmail: userData.recovery_email,
                timezone: userData.timezone,
                language: userData.language,
                themeAccentColor: userData.theme_accent_color,
                skillsBadges: userData.skills_badges,
                referralCode: userData.referral_code,
                supportPin: userData.support_pin,
                dateFormat: userData.date_format,
                numberFormat: userData.number_format,
                requirePasswordReset: userData.require_password_reset,
                gamificationXp: userData.gamification_xp,
                gamificationLevel: userData.gamification_level,
                gamificationTotalSpent: userData.gamification_total_spent,
                gamificationPermanentDiscount: userData.gamification_permanent_discount,
                streakDays: userData.streak_days,
                streakBest: userData.streak_best,
                paypalSubscriptionId: userData.paypal_subscription_id,
            };

            return { valid: true, user: mappedUser, token };
        } catch {
            return { valid: false };
        }
    },

    register: async (
        name: string,
        email: string,
        pass: string,
        phone: string,
        ref_code?: string,
        phoneCountry?: string,
        requestedReturnPath?: string,
    ): Promise<User | PhoneVerificationPending | void> => {
        const gclid = localStorage.getItem('tgp_gclid') || undefined;
        const response = await fetch(`${API_BASE_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email, password: pass, phone, phone_country: phoneCountry, brand_origin: brandConfig.id, ref_code, gclid })
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Registration failed'));
        }
        const data = await response.json();

        if (data.status === 'requires_phone_verification') {
            return {
                status: 'requires_phone_verification',
                pendingRegistrationId: data.pending_registration_id,
                email: data.email,
                phone: data.phone,
                verificationChannel: data.verification_channel || 'sms',
                verificationProvider: data.verification_provider || 'twilio',
                grantsCredits: data.grants_credits !== false,
                message: data.message || 'We sent a verification code to your phone.',
            };
        }

        // Clean up the GCLID after successful registration
        if (gclid) {
            localStorage.removeItem('tgp_gclid');
        }

        if (isSsoEnabled()) {
            return db.login(email, pass, false, requestedReturnPath);
        }
    },

    verifyPhoneRegistration: async (
        pendingRegistrationId: string,
        code: string,
        provider: 'self_hosted' | 'twilio' = 'self_hosted',
    ): Promise<PhoneVerificationComplete> => {
        const path = provider === 'twilio' ? 'verify-phone' : 'verify-code';
        const response = await fetch(`${API_BASE_URL}/auth/register/${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pending_registration_id: pendingRegistrationId, code })
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Phone verification failed'));
        }
        const data = await response.json();
        localStorage.removeItem('tgp_gclid');
        localStorage.removeItem('tgp_ref_code');
        return {
            status: 'verified',
            email: data.email,
            message: data.message || 'Phone verified successfully',
        };
    },

    resendPhoneRegistration: async (
        pendingRegistrationId: string,
        provider: 'self_hosted' | 'twilio' = 'self_hosted',
    ): Promise<PhoneVerificationPending> => {
        const path = provider === 'twilio' ? 'resend-phone' : 'resend-code';
        const response = await fetch(`${API_BASE_URL}/auth/register/${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pending_registration_id: pendingRegistrationId })
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to resend verification code'));
        }
        const data = await response.json();
        return {
            status: 'requires_phone_verification',
            pendingRegistrationId,
            email: data.email,
            phone: data.phone,
            verificationChannel: data.verification_channel || 'sms',
            verificationProvider: data.verification_provider || provider,
            grantsCredits: data.grants_credits !== false,
            message: data.message || 'We sent a new verification code to your phone.',
        };
    },

    verifyEmail: async (code: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Email verification failed');
        }
        const data = await response.json();
        const currentUser = db.getCurrentUser();
        if (currentUser) {
            currentUser.isVerified = true;
            if (typeof data.balance_economy === 'number') {
                currentUser.balanceEconomy = data.balance_economy;
            }
            db.setCurrentUser(currentUser);
        }
        return data;
    },

    resendVerificationEmail: async (email: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to resend verification email');
        }
        return await response.json();
    },

    forgotPassword: async (email: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to send reset email');
        }
        return await response.json();
    },

    resetPassword: async (token: string, newPassword: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, new_password: newPassword })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Password reset failed');
        }
        return await response.json();
    },

    // Projects
    getProjects: (): Project[] => {
        const data = localStorage.getItem('modus_projects_cache');
        const activeUserId = getActiveUserId();
        if (!activeUserId) return [];
        const backendProjects: Project[] = data
            ? (JSON.parse(data) as Project[]).filter((project) => project.userId === activeUserId)
            : [];
        const previews = getLocalPreviewProjects();
        return [
            ...previews,
            ...backendProjects.filter((project) => !previews.some((preview) => preview.id === project.id)),
        ];
    },

    isLocalPreviewEnabled,

    addLocalPreviewProject: async (project: Project): Promise<Project> => {
        if (!isLocalPreviewEnabled()) {
            throw new Error('Frontend campaign previews are only available on localhost in development mode.');
        }
        const activeUserId = getActiveUserId();
        if (!activeUserId) {
            throw new Error('You must be signed in before creating a campaign.');
        }
        if (project.userId && project.userId !== activeUserId) {
            throw new Error('Campaign owner does not match the signed-in user.');
        }
        const previewProject: Project = {
            ...project,
            userId: activeUserId,
            id: project.id.startsWith('preview_') ? project.id : `preview_${project.id}`,
            status: 'paused',
            isFrontendPreview: true,
        };
        const previews = getLocalPreviewProjects();
        saveLocalPreviewProjects([
            previewProject,
            ...previews.filter((existing) => existing.id !== previewProject.id),
        ]);
        return previewProject;
    },

    getAdminProjects: (): Project[] => {
        const data = localStorage.getItem('modus_admin_projects_cache');
        return data ? JSON.parse(data) : [];
    },

    syncProjects: async () => {
        try {
            // Fetch projects + stats in PARALLEL (was sequential — saves ~100-200ms)
            const [response, statsResponse] = await Promise.all([
                fetchWithAuth(`${API_BASE_URL}/projects`),
                fetchWithAuth(`${API_BASE_URL}/projects/stats?days=30`).catch(() => null),
            ]);
            if (!response.ok) return;
            const data = await response.json();

            let statsData: Record<string, any[]> = {};
            if (statsResponse && statsResponse.ok) {
                try { statsData = await statsResponse.json(); } catch (e) { /* ignore */ }
            }

            const mapped: Project[] = data.map((p: any) => mapProjectResponse(p, statsData[p.id] || []));

            localStorage.setItem('modus_projects_cache', JSON.stringify(mapped));
            localStorage.setItem('_projects_last_sync', Date.now().toString());
        } catch (e) {
            console.error("Failed to sync projects:", e);
        }
    },

    syncProjectStats: async (projectId: string, days: number = 30): Promise<{ date: string; visitors: number; pageviews: number }[]> => {
        if (getLocalPreviewProject(projectId)) return [];
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/projects/${projectId}/stats?days=${days}`);
            if (!response.ok) return [];
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch project stats:", e);
            return [];
        }
    },

    syncProjectStatsHourly: async (projectId: string, hours: number = 24): Promise<{ hour: string; visitors: number; pageviews: number }[]> => {
        if (getLocalPreviewProject(projectId)) return [];
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/projects/${projectId}/stats/hourly?hours=${hours}`);
            if (!response.ok) return [];
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch hourly project stats:", e);
            return [];
        }
    },

    getProjectAnalytics: async (
        projectId: string,
        days: number = 30,
        alsoIds: string[] = [],
        range?: { from: string; to: string },
    ): Promise<ProjectAnalyticsResponse | null> => {
        if (getLocalPreviewProject(projectId)) return null;
        try {
            const also = alsoIds.length ? `&also=${alsoIds.join(',')}` : '';
            // An explicit range replaces the preset; the endpoint ignores days
            // when from/to arrive, and sending both would only invite drift.
            const window = range ? `from=${range.from}&to=${range.to}` : `days=${days}`;
            const response = await fetchWithAuth(`${API_BASE_URL}/projects/${projectId}/analytics?${window}${also}`);
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch project analytics:", e);
            return null;
        }
    },

    /**
     * Downloads the analytics export. The endpoint needs the bearer token, so
     * a plain <a href> would 401 — the file has to come through fetch and be
     * handed to the browser as a blob.
     */
    downloadProjectAnalytics: async (
        projectId: string,
        format: 'pdf' | 'xlsx' | 'csv' | 'json',
        days: number = 30,
        alsoIds: string[] = [],
        range?: { from: string; to: string },
    ): Promise<boolean> => {
        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/projects/${projectId}/analytics/export?fmt=${format}`
                + (range ? `&from=${range.from}&to=${range.to}` : `&days=${days}`)
                + (alsoIds.length ? `&also=${alsoIds.join(',')}` : ''),
            );
            if (!response.ok) return false;

            const disposition = response.headers.get('content-disposition') || '';
            const match = disposition.match(/filename="([^"]+)"/);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = match?.[1] || `analytics.${format}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Revoking immediately can cancel the download in Safari; one tick
            // is enough for the navigation to have started.
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            return true;
        } catch (e) {
            console.error('Failed to download project analytics:', e);
            return false;
        }
    },

    /** Server-side search over the long tail the report itself truncates. */
    searchProjectAnalytics: async (
        projectId: string,
        kind: 'pages' | 'cities',
        query: string,
        opts: { days?: number; alsoIds?: string[]; range?: { from: string; to: string } } = {},
    ): Promise<{ total: number; items: { key: string; label: string; sublabel: string | null; value: number }[] } | null> => {
        try {
            const window = opts.range
                ? `from=${opts.range.from}&to=${opts.range.to}`
                : `days=${opts.days ?? 30}`;
            const also = opts.alsoIds?.length ? `&also=${opts.alsoIds.join(',')}` : '';
            const response = await fetchWithAuth(
                `${API_BASE_URL}/projects/${projectId}/analytics/search`
                + `?kind=${kind}&q=${encodeURIComponent(query)}&${window}${also}`,
            );
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            console.error('Failed to search project analytics:', e);
            return null;
        }
    },

    syncProjectStatsLive: async (projectId: string): Promise<{ time: string; visitors: number; pageviews: number }[]> => {
        if (getLocalPreviewProject(projectId)) return [];
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/projects/${projectId}/stats/live`);
            if (!response.ok) return [];
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch live project stats:", e);
            return [];
        }
    },

    getCreditUsage: async (days: number = 30, projectId?: string): Promise<CreditUsageResponse> => {
        const query = new URLSearchParams({ days: String(days) });
        if (projectId) query.set('project_id', projectId);
        const response = await fetchWithAuth(`${API_BASE_URL}/projects/credit-usage?${query.toString()}`);
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to load credit usage'));
        }
        return response.json();
    },

    getCalculatedExpiry: async (projectId: string): Promise<{
        daysRemaining: number | null;
        expiresDate: string | null;
        balance: number;
        totalDailyConsumption: number;
        message: string | null;
    }> => {
        if (getLocalPreviewProject(projectId)) {
            return {
                daysRemaining: null,
                expiresDate: null,
                balance: 0,
                totalDailyConsumption: 0,
                message: 'Frontend preview',
            };
        }
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/projects/${projectId}/expires-calculated`);
            if (!response.ok) {
                return { daysRemaining: null, expiresDate: null, balance: 0, totalDailyConsumption: 0, message: 'Failed to calculate' };
            }
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch calculated expiry:", e);
            return { daysRemaining: null, expiresDate: null, balance: 0, totalDailyConsumption: 0, message: 'Error' };
        }
    },

    getActiveNow: async (): Promise<{ activeNow: number; projects: Record<string, { active: number; lastHit?: string }> }> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/projects/active-now`);
            if (!response.ok) return { activeNow: 0, projects: {} };
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch active now:", e);
            return { activeNow: 0, projects: {} };
        }
    },

    syncAdminProjects: async (limit: number = 25, offset: number = 0) => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects?limit=${limit}&offset=${offset}`);
            if (!response.ok) return { projects: [], total: 0 };
            const result = await response.json();
            const data = result.projects || [];

            let statsData: Record<string, any[]> = {};
            try {
                const statsResponse = await fetchWithAuth(`${API_BASE_URL}/projects/stats?days=30`);
                if (statsResponse.ok) {
                    statsData = await statsResponse.json();
                }
            } catch (e) {
                console.error("Failed to fetch project stats:", e);
            }

            const mapped: Project[] = data.map((p: any) => mapProjectResponse(p, statsData[p.id] || []));

            localStorage.setItem('modus_admin_projects_cache', JSON.stringify(mapped));
            return { projects: mapped, total: result.total || 0, limit, offset };
        } catch (e) {
            console.error("Failed to sync admin projects:", e);
            return { projects: [], total: 0 };
        }
    },

    bulkUpdateAdminProjects: async (projectIds: string[], updates: { status?: string; priority?: number }): Promise<void> => {
        try {
            await Promise.all(projectIds.map(async (id) => {
                const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(updates)
                });
                if (!response.ok) {
                    console.error(`Failed to update project ${id}`);
                }
            }));
            await db.syncAdminProjects();
        } catch (e) {
            console.error("Failed to bulk update projects:", e);
        }
    },

    deleteProjectAdmin: async (id: string): Promise<void> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/${id}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                throw new Error("Failed to delete project as admin");
            }
            await db.syncAdminProjects();
        } catch (e) {
            console.error("Delete project error:", e);
            throw e;
        }
    },

    bulkDeleteAdminProjects: async (projectIds: string[]): Promise<void> => {
        try {
            await Promise.all(projectIds.map(async (id) => {
                await fetchWithAuth(`${API_BASE_URL}/admin/projects/${id}`, {
                    method: 'DELETE'
                });
            }));
            await db.syncAdminProjects();
        } catch (e) {
            console.error("Bulk delete error:", e);
            throw e;
        }
    },

    getProjectById: (id: string): Project | undefined => {
        const projects = db.getProjects();
        let project = projects.find(p => p.id === id);
        if (!project && db.getCurrentUser()?.role === 'admin') {
            const adminProjects = db.getAdminProjects();
            project = adminProjects.find(p => p.id === id);
        }
        return project;
    },

    syncProjectById: async (id: string): Promise<Project | undefined> => {
        const preview = getLocalPreviewProject(id);
        if (preview) return preview;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/projects/${id}`);
            if (!response.ok) return undefined;

            const data = await response.json();
            const mapped = mapProjectResponse(data);
            const projects = db.getProjects();
            localStorage.setItem(
                'modus_projects_cache',
                JSON.stringify([mapped, ...projects.filter(p => p.id !== id)])
            );
            return mapped;
        } catch (e) {
            console.error("Failed to sync project:", e);
            return undefined;
        }
    },

    fetchAdminProjectById: async (id: string): Promise<Project | undefined> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/${id}`);
            if (!response.ok) return undefined;

            const mapped = mapProjectResponse(await response.json());
            const projects = db.getAdminProjects();
            localStorage.setItem(
                'modus_admin_projects_cache',
                JSON.stringify([mapped, ...projects.filter(p => p.id !== id)])
            );
            return mapped;
        } catch (e) {
            console.error("Failed to fetch admin project:", e);
            return undefined;
        }
    },

    addProject: async (project: Project) => {
        const projectData = {
            name: project.name,
            plan_type: project.plan,
            tier: project.tier || 'economy',
            settings: normalizeProjectUrlSettings(project.settings || {}),
            daily_limit: project.customTarget?.dailyLimit || project.dailyLimit || 1000,
            total_target: project.customTarget?.totalVisitors ?? project.totalTarget ?? 5000,
            start_at: project.startAt || null
        };

        const response = await fetchWithAuth(`${API_BASE_URL}/projects`, {
            method: 'POST',
            body: JSON.stringify(projectData)
        });

        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to create project'));
        }

        const createdProject = await response.json();
        await db.syncProjects();
        return createdProject;
    },

    // Admin: Create project for a user (bypasses email verification and can deduct credits)
    addProjectAdmin: async (project: Project, userEmail: string, deductCredits: boolean = false) => {
        const projectData = {
            name: project.name,
            plan_type: project.plan,
            tier: project.tier || 'economy',
            settings: normalizeProjectUrlSettings(project.settings || {}),
            daily_limit: project.customTarget?.dailyLimit || project.dailyLimit || 1000,
            total_target: project.customTarget?.totalVisitors || project.totalTarget || 5000,
            start_at: project.startAt || null,
            user_email: userEmail,
            deduct_credits: deductCredits
        };

        const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects`, {
            method: 'POST',
            body: JSON.stringify(projectData)
        });

        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to create project as admin'));
        }

        const createdProject = await response.json();
        await db.syncProjects();
        await db.syncAdminProjects();
        return createdProject;
    },

    updateProject: async (project: Project) => {
        if (project.isFrontendPreview && isLocalPreviewEnabled()) {
            const updatedProject = {
                ...project,
                settings: normalizeProjectUrlSettings(project.settings),
            };
            const previews = getLocalPreviewProjects();
            saveLocalPreviewProjects([
                updatedProject,
                ...previews.filter((existing) => existing.id !== project.id),
            ]);
            return updatedProject;
        }
        const dailyLimit = project.settings?.scheduleTrafficAmount || project.customTarget?.dailyLimit || 0;
        const isContinuous = project.settings?.goalMode === 'continuous' || project.settings?.continuousDelivery === true;

        const payload = {
            name: project.name,
            settings: normalizeProjectUrlSettings(project.settings),
            daily_limit: dailyLimit,
            total_target: isContinuous ? 0 : (project.customTarget?.totalVisitors || project.totalTarget || 0),
            status: project.status,
            tier: project.tier,
            settings_version: project.settingsVersion,
        };

        const response = await fetchWithAuth(`${API_BASE_URL}/projects/${project.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to update project'));
        }

        const updatedProject = mapProjectResponse(await response.json());
        await db.syncProjects();
        return updatedProject;
    },

    updateProjectStatus: async (id: string, status: 'active' | 'stopped' | 'completed') => {
        const preview = getLocalPreviewProject(id);
        if (preview) {
            const updatedProject: Project = { ...preview, status };
            saveLocalPreviewProjects([
                updatedProject,
                ...getLocalPreviewProjects().filter((existing) => existing.id !== id),
            ]);
            return updatedProject;
        }
        const endpoint = status === 'active' ? 'start' : 'stop';
        const response = await fetchWithAuth(`${API_BASE_URL}/projects/${id}/${endpoint}`, { method: 'POST' });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to ${endpoint} project`);
        }
        const updatedProject = mapProjectResponse(await response.json());
        await db.syncProjects();
        return updatedProject;
    },

    changeProjectTier: async (id: string, tier: 'professional' | 'expert') => {
        const response = await fetchWithAuth(`${API_BASE_URL}/projects/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ tier }),
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to change the campaign wallet'));
        }
        const updatedProject = mapProjectResponse(await response.json());
        await db.syncProjects();
        return updatedProject;
    },

    deleteProject: async (id: string) => {
        const preview = getLocalPreviewProject(id);
        if (preview) {
            saveLocalPreviewProjects(getLocalPreviewProjects().filter((project) => project.id !== id));
            return db.getProjects();
        }
        const response = await fetchWithAuth(`${API_BASE_URL}/projects/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.detail || "Failed to delete project");
        }
        await db.syncProjects();
        return db.getProjects();
    },

    cloneProject: async (id: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/projects/${id}/clone`, { method: 'POST' });
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.detail || 'Failed to clone project');
        }
        await db.syncProjects();
        return db.getProjects();
    },

    // Admin Project Update - bypasses user ownership check
    updateProjectAdmin: async (project: Project) => {
        const payload = {
            name: project.name,
            settings: normalizeProjectUrlSettings(project.settings),
            daily_limit: project.daily_limit || project.customTarget?.dailyLimit || 0,
            total_target: project.total_target || project.customTarget?.totalVisitors || 0,
            status: project.status,
            tier: project.tier,
            plan_type: project.plan,
            expires: project.expires,
            priority: project.settings?.adminPriority || 0,
            force_stop_reason: project.settings?.forceStopReason || '',
            settings_version: project.settingsVersion,
            deduct_credits: project.deductCredits
        };

        const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/${project.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to update project as admin'));
        }

        const updatedProject = mapProjectResponse(await response.json());
        await db.syncProjects();
        await db.syncAdminProjects();
        return updatedProject;
    },

    // Admin: Update project status directly (bypasses user ownership)
    updateProjectStatusAdmin: async (projectId: string, status: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/${projectId}`, {
            method: 'PUT',
            body: JSON.stringify({ status })
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to update project status'));
        }
        const updatedProject = mapProjectResponse(await response.json());
        await db.syncProjects();
        await db.syncAdminProjects();
        return updatedProject;
    },

    // Bulk Actions
    bulkUpdateProjectStatus: async (ids: string[], status: 'active' | 'stopped') => {
        const results = await Promise.all(ids.map(id =>
            fetchWithAuth(`${API_BASE_URL}/projects/${id}/${status === 'active' ? 'start' : 'stop'}`, { method: 'POST' })
        ));

        const firstErr = results.find(r => !r.ok);
        if (firstErr) {
            const errData = await firstErr.json().catch(() => ({}));
            throw new Error(errData.detail || `Failed to ${status === 'active' ? 'start' : 'stop'} projects`);
        }

        await db.syncProjects();
        return db.getProjects();
    },

    bulkDeleteProjects: async (ids: string[]) => {
        const results = await Promise.all(ids.map(id =>
            fetchWithAuth(`${API_BASE_URL}/projects/${id}`, { method: 'DELETE' })
        ));

        const firstErr = results.find(r => !r.ok);
        if (firstErr) {
            const errData = await firstErr.json().catch(() => ({}));
            throw new Error(errData.detail || "Failed to delete projects");
        }

        await db.syncProjects();
        return db.getProjects();
    },

    getMaxTierBalance: (): number => {
        const user = db.getCurrentUser();
        const balances = [
            user?.balanceEconomy ?? 0,
            user?.balanceProfessional ?? 0,
            user?.balanceExpert ?? 0
        ];
        return Math.max(...balances);
    },

    // Financials
    getBalance: (): number => {
        return db.getMaxTierBalance();
    },

    // Unified Syncing (admin — all data including admin endpoints)
    syncAll: async () => {
        await Promise.all([
            db.syncProjects(),
            db.syncTransactions(),
            db.syncSettings(),
            db.syncUsers(),
            db.syncAllTransactions(),
            db.syncTickets(),
            db.syncNotifications(),
            db.syncAlerts()
        ]);
    },

    // ⚡ Lightweight sync for regular user login — only what's actually needed
    // Avoids admin-only endpoints (syncUsers, syncAllTransactions) that return 403
    // and duplicate calls. Down from 9 API calls to 3.
    syncUserData: async () => {
        await Promise.all([
            db.syncProjects(),
            db.syncTransactions(),
            db.syncNotifications(),
        ]);
    },

    // Users
    getUsers: (): User[] => {
        const data = localStorage.getItem('modus_users_cache');
        return data ? JSON.parse(data) : [];
    },

    getAdmins: (): User[] => {
        const users = db.getUsers();
        return users.filter(u => u.role === 'admin');
    },

    syncUsers: async () => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users`);
        if (!response.ok) return [];
        const data = await response.json();
        // Backend may return paginated response {users: [...], total, limit, offset} or plain array
        const rawUsers = Array.isArray(data) ? data : (data.users || []);
        const mapped = rawUsers.map((u: any) => ({
            id: u.id,
            email: u.email,
            name: u.name || u.email.split('@')[0],
            role: u.role,
            balance: u.balance,
            balanceEconomy: u.balance_economy,
            balanceProfessional: u.balance_professional,
            balanceExpert: u.balance_expert,
            balanceSeo: u.balance_seo,
            status: u.status || 'active',
            joinedDate: u.created_at?.split('T')[0] || u.joinedDate || new Date().toISOString().split('T')[0],
            projectsCount: u.projects_count || 0,
            plan: u.plan || 'free',
            shadowBanned: u.shadow_banned || false,
            isVerified: u.is_verified || false,
            notes: u.notes || '',
            tags: u.tags || [],
            banReason: u.ban_reason || '',
            lastIp: u.last_ip || '',
            lastActive: u.last_active || '',
            spamScore: u.spam_score || 0,
            ipSharedWithCount: u.ip_shared_with_count || 0,
            affiliateEarnings: u.affiliate_earnings || 0,
            createdAt: u.created_at,
        }));
        // Note: admin users API may return paginated responses.
        // This is kept for backward compat with non-paginated endpoints or as fallback.
        localStorage.setItem('modus_users_cache', JSON.stringify(mapped));
        return mapped;
    },

    getPaginatedUsers: async (limit: number = 50, offset: number = 0, search?: string): Promise<PaginatedUsersResponse> => {
        let url = `${API_BASE_URL}/admin/users?limit=${limit}&offset=${offset}`;
        if (search) {
            url += `&search=${encodeURIComponent(search)}`;
        }
        const response = await fetchWithAuth(url);
        if (!response.ok) throw new Error('Failed to fetch users');
        const data = await response.json();

        const mappedUsers = data.users.map((u: any) => ({
            ...u,
            name: u.name || u.email.split('@')[0],
            joinedDate: u.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
            createdAt: u.created_at,
        }));

        return {
            users: mappedUsers,
            total: data.total,
            limit: data.limit,
            offset: data.offset
        };
    },

    deleteUserAdmin: async (userId: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error("Failed to delete user");
    },

    getUserById: (id: string): User | undefined => {
        const users = db.getUsers();
        return users.find(u => u.id === id);
    },

    getUserStats: async (): Promise<UserStats | null> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/stats`);
            if (!response.ok) return null;
            const data = await response.json();
            return {
                totalUsers: data.total_users,
                activeUsers24h: data.active_users_24h,
                activeUsers7d: data.active_users_7d,
                newUsersToday: data.new_users_today,
                newUsers7d: data.new_users_7d,
                newUsers30d: data.new_users_30d,
                highRiskUsers: data.high_risk_users,
                fraudAlertsCount: data.fraud_alerts_count
            };
        } catch (e) {
            console.error('Failed to fetch user stats:', e);
            return null;
        }
    },

    getAdminOverview: async (): Promise<any | null> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/overview`);
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch admin overview", e);
            return null;
        }
    },

    // ⚡ Combined dashboard init — replaces 3 separate API calls
    getDashboardInit: async (): Promise<{
        gamification: any;
        streak: any;
        activeNow: number;
    } | null> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/users/me/dashboard-init`);
            if (!response.ok) return null;
            const data = await response.json();
            return {
                gamification: data.gamification,
                streak: data.streak,
                activeNow: data.active_now,
            };
        } catch (e) {
            console.error("Failed to fetch dashboard init", e);
            return null;
        }
    },

    getFraudAlerts: async (): Promise<FraudAlert[]> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/fraud-alerts`);
            if (!response.ok) return [];
            const data = await response.json();
            return data.map((a: any) => ({
                id: a.id,
                type: a.type,
                ip: a.ip,
                userIds: a.user_ids,
                userEmails: a.user_emails,
                userDetails: a.user_details,
                affiliateEarnings: a.affiliate_earnings,
                hasAffiliateRelation: a.has_affiliate_relation,
                detectedAt: a.detected_at,
                riskLevel: a.risk_level
            }));
        } catch (e) {
            console.error('Failed to fetch fraud alerts:', e);
            return [];
        }
    },

    dismissFraudAlerts: async (ips: string[]): Promise<boolean> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/fraud-alerts/dismiss`, {
                method: 'POST',
                body: JSON.stringify({ ips })
            });
            return response.ok;
        } catch (e) {
            console.error('Failed to dismiss fraud alerts:', e);
            return false;
        }
    },

    getTopSpenders: async (limit: number = 10): Promise<any[]> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/top-spenders?limit=${limit}`);
            if (!response.ok) return [];
            return await response.json();
        } catch (e) {
            console.error('Failed to fetch top spenders:', e);
            return [];
        }
    },

    getGrowthStats: async (): Promise<any> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/stats/growth`);
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            console.error('Failed to fetch growth stats:', e);
            return null;
        }
    },

    updateUser: async (user: User) => {
        const users = db.getUsers();
        const index = users.findIndex(u => u.id === user.id);
        if (index >= 0) {
            users[index] = user;
            localStorage.setItem('modus_users_cache', JSON.stringify(users));
        }
        return user;
    },



    // Transactions
    getTransactions: (): Transaction[] => {
        const data = localStorage.getItem('modus_transactions_cache');
        return data ? JSON.parse(data) : [];
    },

    syncTransactions: async () => {
        const response = await fetchWithAuth(`${API_BASE_URL}/transactions`);
        if (!response.ok) return [];
        const data = await response.json();
        const mapped: Transaction[] = data.map((t: any) => ({
            id: t.id,
            date: t.created_at?.split('T')[0] || t.date || new Date().toISOString().split('T')[0],
            desc: t.description || t.desc,
            amount: t.amount,
            status: t.status || 'completed',
            type: t.type,
            userId: t.user_id || t.userId,
            tier: t.tier,
            hits: t.hits,
            reference: t.reference,
            paymentMethod: t.payment_method
        }));
        localStorage.setItem('modus_transactions_cache', JSON.stringify(mapped));
        return mapped;
    },

    getAllTransactionsAdmin: (): Transaction[] => {
        const data = localStorage.getItem('modus_admin_transactions_cache');
        return data ? JSON.parse(data) : [];
    },

    syncAllTransactions: async () => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/transactions`);
        if (!response.ok) return [];
        const data = await response.json();
        // Backend returns paginated response {transactions: [...], total, limit, offset}
        const rawTransactions = Array.isArray(data) ? data : (data.transactions || []);
        const mapped = rawTransactions.map((t: any) => ({
            id: t.id,
            date: t.created_at?.split('T')[0] || t.date || new Date().toISOString().split('T')[0],
            desc: t.description || t.desc,
            amount: t.amount,
            status: t.status || 'completed',
            type: t.type,
            userId: t.user_id || t.userId,
            userEmail: t.user_email || null,
            tier: t.tier,
            hits: t.hits,
            reference: t.reference,
            paymentMethod: t.payment_method,
            user_id: t.userId || t.user_id, // ensure user_id is populated for typing
        }));
        localStorage.setItem('modus_admin_transactions_cache', JSON.stringify(mapped));
        return mapped;
    },

    getPaginatedTransactions: async (limit: number = 50, offset: number = 0): Promise<PaginatedTransactionsResponse> => {
        const url = `${API_BASE_URL}/admin/transactions?limit=${limit}&offset=${offset}`;
        const response = await fetchWithAuth(url);
        if (!response.ok) throw new Error('Failed to fetch transactions');
        const data = await response.json();

        const mappedTransactions = data.transactions.map((t: any) => ({
            ...t,
            date: t.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
            desc: t.description || t.desc,
            userId: t.user_id || t.userId,
            user_id: t.user_id || t.userId, // ensure map
            userEmail: t.user_email || t.userEmail,
            paymentMethod: t.payment_method,
        }));

        return {
            transactions: mappedTransactions,
            total: data.total,
            limit: data.limit,
            offset: data.offset
        };
    },

    createPaymentIntent: async (amount: number, currency: string = 'eur') => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const response = await fetchObserved(`${API_BASE_URL}/create-payment-intent`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ amount, currency })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create payment intent');
        }
        return await response.json();
    },

    // Stripe Subscriptions
    createCheckoutSession: async (priceId: string) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const currentOrigin = window.location.origin;
        const response = await fetchObserved(`${API_BASE_URL}/subscriptions/create-checkout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                price_id: priceId,
                success_url: `${currentOrigin}/dashboard/payment-success?type=subscription&session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${currentOrigin}/dashboard/buy-credits?payment=cancelled&checkout=subscription`
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create checkout session');
        }
        return await response.json();
    },

    createCreditsCheckout: async (params: {
        tier: 'economy' | 'professional' | 'expert';
        visitors: number;
        bulk_months: number;
        currency?: string;
        coupon_code?: string;
    }) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const currentOrigin = window.location.origin;
        const response = await fetchObserved(`${API_BASE_URL}/stripe/create-checkout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                tier: params.tier,
                visitors: params.visitors,
                bulk_months: params.bulk_months,
                currency: params.currency || 'usd',
                coupon_code: params.coupon_code,
                success_url: `${currentOrigin}/dashboard/payment-success?type=credits&session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${currentOrigin}/dashboard/buy-credits?payment=cancelled`
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create checkout session');
        }
        return await response.json();
    },

    verifyStripeCheckoutSession: async (sessionId: string): Promise<{
        verified: boolean;
        status: string;
        payment_status: string;
        type: 'subscription' | 'credits' | 'gsc' | 'local';
        amount: number;
        currency: 'EUR' | 'USD';
        credits: number;
        session_id: string;
    }> => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error('Not authenticated');
        const response = await fetchObserved(`${API_BASE_URL}/stripe/checkout-session/${encodeURIComponent(sessionId)}`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Failed to verify checkout session');
        }
        return await response.json();
    },

    createPremiumCheckout: async (params: {
        type: 'gsc' | 'local';
        credits: number;
    }) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const currentOrigin = window.location.origin;
        const response = await fetchObserved(`${API_BASE_URL}/stripe/create-premium-checkout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                type: params.type,
                credits: params.credits,
                success_url: `${currentOrigin}/dashboard/payment-success?type=${params.type}&session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${currentOrigin}/dashboard/${params.type === 'gsc' ? 'gsc-traffic' : 'local-seo-traffic'}?payment=cancelled`
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create premium checkout session');
        }
        return await response.json();
    },

    createPayPalPremiumCheckout: async (params: {
        type: 'gsc' | 'local';
        credits: number;
    }) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const currentOrigin = window.location.origin;
        const response = await fetch(`${API_BASE_URL}/paypal/create-premium-order`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                type: params.type,
                credits: params.credits,
                success_url: `${currentOrigin}/dashboard/payment-success?type=${params.type}&paypal_order=true`,
                cancel_url: `${currentOrigin}/dashboard/${params.type === 'gsc' ? 'gsc-traffic' : 'local-seo-traffic'}?payment=cancelled`
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create PayPal checkout');
        }
        return await response.json();
    },

    createPayPalCreditsCheckout: async (params: {
        tier: 'economy' | 'professional' | 'expert';
        visitors: number;
        bulk_months: number;
        coupon_code?: string;
    }) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error('Not authenticated');
        const currentOrigin = window.location.origin;
        const response = await fetch(`${API_BASE_URL}/paypal/create-credits-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                ...params,
                currency: 'USD',
                success_url: `${currentOrigin}/dashboard/payment-success?type=credits&paypal_order=true`,
                cancel_url: `${currentOrigin}/dashboard/buy-credits?payment=cancelled`,
            }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Failed to create PayPal checkout');
        }
        return await response.json();
    },

    capturePayPalPremiumCheckout: async (orderId: string) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const response = await fetch(`${API_BASE_URL}/paypal/capture-premium-order`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ order_id: orderId })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to capture PayPal order');
        }
        return await response.json();
    },

    createPortalSession: async (scope: 'current' | 'legacy' = 'current') => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const currentOrigin = window.location.origin;
        const response = await fetch(`${API_BASE_URL}/subscriptions/portal`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                return_url: `${currentOrigin}/dashboard`,
                scope
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create portal session');
        }
        return await response.json();
    },

    /**
     * Subscriptions still billing on a pre-migration Stripe customer. Users who
     * hit the EUR->USD currency lock got a fresh customer, and a portal session
     * only ever covers one customer — so these need their own entry point.
     */
    getLegacySubscriptions: async (): Promise<{ customer_id: string | null; subscriptions: LegacySubscription[] }> => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const response = await fetch(`${API_BASE_URL}/subscriptions/legacy`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) return { customer_id: null, subscriptions: [] };
        return await response.json();
    },

    getCurrentSubscription: async () => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error("Not authenticated");

        const response = await fetch(`${API_BASE_URL}/subscriptions/current`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to get subscription');
        }
        return await response.json();
    },

    // PayPal Subscriptions
    createPayPalSubscription: async (params: {
        tier: 'economy' | 'professional' | 'expert';
        visitors: number;
        bulk_months: number;
    }) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error('Not authenticated');
        const currentOrigin = window.location.origin;
        const response = await fetch(`${API_BASE_URL}/paypal/create-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                ...params,
                success_url: `${currentOrigin}/dashboard/payment-success?type=credits&session_id=paypal`,
                cancel_url: `${currentOrigin}/dashboard/buy-credits?payment=cancelled`,
            }),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to create PayPal subscription');
        }
        return await response.json();
    },

    activatePayPalSubscription: async (params: {
        subscription_id: string;
        tier: string;
        visitors: number;
        bulk_months: number;
    }) => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error('Not authenticated');
        const response = await fetch(`${API_BASE_URL}/paypal/activate-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(params),
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to activate PayPal subscription');
        }
        return await response.json();
    },

    cancelPayPalSubscription: async () => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error('Not authenticated');
        const response = await fetch(`${API_BASE_URL}/paypal/cancel-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to cancel PayPal subscription');
        }
        return await response.json();
    },

    getPayPalSubscription: async () => {
        const token = localStorage.getItem('tgp_token');
        if (!token) throw new Error('Not authenticated');
        const response = await fetch(`${API_BASE_URL}/paypal/subscription/current`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) return null;
        return await response.json();
    },

    purchaseCredits: async (amount: number, description: string, tier?: string, hits?: number) => {
        const user = db.getCurrentUser();
        if (!user) return 0;

        await fetchWithAuth(`${API_BASE_URL}/webhooks/deposit`, {
            method: 'POST',
            body: JSON.stringify({
                user_email: user.email,
                amount: amount,
                description: description,
                tier: tier,
                hits: hits
            })
        });

        const userRes = await fetchWithAuth(`${API_BASE_URL}/users/me`);
        const userData = await userRes.json();
        db.setCurrentUser({
            ...user,
            balance: userData.balance,
            balanceEconomy: userData.balance_economy,
            balanceProfessional: userData.balance_professional,
            balanceExpert: userData.balance_expert,
            balanceSeo: userData.balance_seo
        });
        await db.syncTransactions();
        return userData.balance; // We might want to return the specific balance but keeping signature for now
    },

    // Tickets
    getTickets: (filters?: { status?: string; category?: string; search?: string }): Ticket[] => {
        const data = localStorage.getItem('modus_tickets_cache');
        let tickets: Ticket[] = data ? JSON.parse(data) : [];

        if (filters) {
            if (filters.status) {
                tickets = tickets.filter(t => t.status === filters.status);
            }
            if (filters.category) {
                tickets = tickets.filter(t => t.category === filters.category);
            }
            if (filters.search) {
                const searchLower = filters.search.toLowerCase();
                tickets = tickets.filter(t =>
                    t.subject.toLowerCase().includes(searchLower) ||
                    t.lastMessage?.toLowerCase().includes(searchLower) ||
                    t.messages?.some(m => m.text.toLowerCase().includes(searchLower))
                );
            }
        }

        return tickets;
    },

    syncTickets: async (filters?: { status?: string; category?: string; search?: string }) => {
        let url = `${API_BASE_URL}/tickets`;
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.category) params.append('category', filters.category);
        if (filters?.search) params.append('search', filters.search);
        const guestId = !getStorageItem('tgp_token') ? getStorageItem('modus_guest_id') : null;
        if (guestId) params.append('guest_id', guestId);
        if (params.toString()) url += `?${params.toString()}`;

        const response = await fetchWithAuth(url);
        if (!response.ok) return;
        const data = await response.json();
        const mapped: Ticket[] = data.map((t: any) => ({
            id: t.id,
            supportNumber: t.support_number,
            supportCode: t.support_code,
            type: t.type || 'ticket',
            userId: t.user_id,
            userName: t.user_name || t.guest_name || t.user_email?.split('@')[0] || t.guest_email?.split('@')[0] || 'User',
            userEmail: t.user_email,
            guestEmail: t.guest_email,
            guestPhone: t.guest_phone,
            subject: t.subject,
            status: t.status || 'open',
            priority: t.priority || 'low',
            category: t.category || 'general',
            source: t.source || 'user',
            assigneeId: t.assignee_id,
            assigneeName: t.assignee_name,
            tags: t.tags || [],
            projectId: t.project_id,
            projectName: t.project_name,
            attachmentUrls: t.attachment_urls || [],
            date: t.created_at?.split('T')[0] || formatDate(new Date()),
            lastMessage: t.messages?.length > 0 ? t.messages[t.messages.length - 1].text : 'Ticket created',
            messages: t.messages || [],
            unread: !!t.unread,
            updatedAt: t.updated_at,
            slaDueAt: t.sla_due_at,
            lastCustomerMessageAt: t.last_customer_message_at,
            lastAdminReplyAt: t.last_admin_reply_at,
            waitingForAdmin: t.waiting_for_admin,
            csatRating: t.csat_rating
        }));
        localStorage.setItem('modus_tickets_cache', JSON.stringify(mapped));
    },

    createTicket: async (ticket: Partial<Ticket>) => {
        const payload = {
            subject: ticket.subject,
            priority: ticket.priority || 'low',
            type: ticket.type || 'ticket',
            category: ticket.category || 'general',
            project_id: ticket.projectId,
            attachment_urls: ticket.attachmentUrls || [],
            messages: ticket.messages || [],
            website: (ticket as any).website || '',
            guest_id: (ticket as any).guest_id || (ticket as any).guestId,
            guest_name: (ticket as any).guest_name || (ticket as any).guestName,
            guest_email: (ticket as any).guest_email || (ticket as any).guestEmail,
            guest_phone: (ticket as any).guest_phone || (ticket as any).guestPhone
        };
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to create ticket'));
        }
        await db.syncTickets();
        return db.getTickets();
    },

    closeTicket: async (ticketId: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}/close`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error("Failed to close ticket");
        await db.syncTickets();
    },

    uploadTicketAttachment: async (file: File): Promise<{ url: string; filename: string }> => {
        const formData = new FormData();
        formData.append('file', file);

        const token = localStorage.getItem('tgp_token');
        const response = await fetch(`${API_BASE_URL}/tickets/upload`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: formData
        });

        if (!response.ok) throw new Error("Failed to upload file");
        return await response.json();
    },

    startPublicChat: async (payload: PublicChatStartPayload): Promise<{ ticket: Ticket; guestToken: string }> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/chat/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to start chat'));
        }
        const data = await response.json();
        return { ticket: mapPublicChatResponse(data), guestToken: data.guest_token };
    },

    getPublicChat: async (ticketId: string, guestToken: string): Promise<Ticket> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/chat/${ticketId}?token=${encodeURIComponent(guestToken)}`);
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to load chat'));
        }
        return mapPublicChatResponse(await response.json());
    },

    replyPublicChat: async (
        ticketId: string,
        guestToken: string,
        message: string,
        attachments: PublicChatAttachment[] = [],
    ): Promise<Ticket> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/chat/${ticketId}/reply?token=${encodeURIComponent(guestToken)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, attachments }),
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to send message'));
        }
        return mapPublicChatResponse(await response.json());
    },

    getPublicChatAdminReply: async (ticketId: string, token: string): Promise<Ticket> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/chat/${ticketId}/admin-reply?token=${encodeURIComponent(token)}`);
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to load chat'));
        }
        return mapPublicChatResponse(await response.json());
    },

    sendPublicChatAdminReply: async (
        ticketId: string,
        token: string,
        message: string,
        attachments: PublicChatAttachment[] = [],
    ): Promise<Ticket> => {
        const response = await fetchObserved(`${API_BASE_URL}/public/chat/${ticketId}/admin-reply?token=${encodeURIComponent(token)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, attachments }),
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to send reply'));
        }
        return mapPublicChatResponse(await response.json());
    },

    uploadPublicChatAttachment: async (
        ticketId: string,
        guestToken: string,
        file: File,
    ): Promise<PublicChatAttachment> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetchObserved(`${API_BASE_URL}/public/chat/${ticketId}/upload?token=${encodeURIComponent(guestToken)}`, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) {
            throw new Error(await readApiError(response, 'Failed to upload attachment'));
        }
        return await response.json();
    },

    getAdminChatInbox: async (filter: string = 'open'): Promise<AdminChatSummary[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/chat/inbox?filter=${encodeURIComponent(filter)}`);
        if (!response.ok) throw new Error(await readApiError(response, 'Failed to load live chats'));
        const data = await response.json();
        return data.chats || [];
    },

    getAdminChatAnalytics: async (from?: string, to?: string): Promise<AdminChatAnalytics> => {
        const params = new URLSearchParams();
        if (from) params.append('from', from);
        if (to) params.append('to', to);
        const suffix = params.toString() ? `?${params.toString()}` : '';
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/chat/analytics${suffix}`);
        if (!response.ok) throw new Error(await readApiError(response, 'Failed to load chat analytics'));
        return await response.json();
    },

    getNotificationHealth: async (): Promise<NotificationHealth> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/notification-health`);
        if (!response.ok) throw new Error(await readApiError(response, 'Failed to load notification health'));
        return await response.json();
    },

    sendNotificationHealthTest: async (): Promise<{ status: string; result: Record<string, boolean> }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/notification-health/test`, { method: 'POST' });
        if (!response.ok) throw new Error(await readApiError(response, 'Failed to send test alert'));
        return await response.json();
    },

    // Notifications
    getNotifications: (): Notification[] => {
        const data = localStorage.getItem('modus_notifications_cache');
        return data ? JSON.parse(data) : [];
    },

    getNotificationUnreadCount: (): number => {
        const cached = Number(localStorage.getItem('modus_notifications_unread_count'));
        if (Number.isFinite(cached) && cached >= 0) return cached;
        return db.getNotifications().filter(notification => !notification.read).length;
    },

    syncNotificationUnreadCount: async (): Promise<number> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications/unread-count`);
        if (!response.ok) return db.getNotificationUnreadCount();
        const data = await response.json();
        const unreadCount = Math.max(0, Number(data.unread_count) || 0);
        localStorage.setItem('modus_notifications_unread_count', String(unreadCount));
        return unreadCount;
    },

    syncNotifications: async (limit: number = 20): Promise<Notification[]> => {
        const safeLimit = Math.max(1, Math.min(100, Math.floor(limit)));
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications?limit=${safeLimit}`);
        if (!response.ok) return db.getNotifications();
        const data = await response.json();
        const mapped = data.map((n: any) => ({
            id: n.id,
            userId: n.user_id,
            title: n.title,
            message: n.message,
            date: n.created_at?.split('T')[0] || formatDate(new Date()),
            read: n.is_read,
            type: n.type || 'info'
        }));
        localStorage.setItem('modus_notifications_cache', JSON.stringify(mapped));
        const unreadHeader = response.headers.get('X-Notification-Unread-Count');
        const unreadCount = unreadHeader === null
            ? mapped.filter((notification: Notification) => !notification.read).length
            : Math.max(0, Number(unreadHeader) || 0);
        localStorage.setItem('modus_notifications_unread_count', String(unreadCount));
        return mapped;
    },

    getAdminNotifications: (): Notification[] => {
        return db.getNotifications();
    },

    markNotificationRead: async (id: string) => {
        await fetchWithAuth(`${API_BASE_URL}/notifications/${id}/read`, { method: 'PUT' });
        await db.syncNotifications();
    },

    markAllNotificationsRead: async () => {
        await fetchWithAuth(`${API_BASE_URL}/notifications/mark-all-read`, { method: 'PUT' });
        await db.syncNotifications();
    },

    // System Settings
    getSystemSettings: (): SystemSettings => {
        /* Diese Funktion wird in Render-Initialisierern aufgerufen (u.a.
           `AddProject`). Ein `JSON.parse`-Wurf hier reisst deshalb die ganze
           Seite in die Error-Boundary — und zwar dauerhaft, weil der kaputte
           Wert im localStorage stehen bleibt: die Seite ist danach fuer
           diesen Browser tot, bis jemand die Websitedaten loescht.
           Beobachtet mit `SyntaxError: "undefined" is not valid JSON` — der
           Cache enthielt den **String** "undefined" (s. `syncSettings`).
           `getCurrentUser` faengt genau diesen Fall seit jeher ab; hier fehlte es. */
        const defaults: SystemSettings = { siteName: brandConfig.name, maintenanceMode: false, allowRegistrations: true, supportEmail: brandConfig.supportEmail, minDeposit: 10 } as SystemSettings;
        const data = localStorage.getItem('modus_settings_cache');
        if (!data) return defaults;
        try {
            const parsed = JSON.parse(data);
            return parsed && typeof parsed === 'object' ? parsed : defaults;
        } catch (e) {
            console.error('Discarding an unreadable settings cache:', e);
            localStorage.removeItem('modus_settings_cache');
            return defaults;
        }
    },

    syncSettings: async () => {
        const response = await fetch(`${API_BASE_URL}/settings`);
        if (!response.ok) return;
        const data = await response.json();
        /* `JSON.stringify(undefined)` ergibt den **String** "undefined" — fehlt
           `settings` in der Antwort, wurde genau das gespeichert und jeder
           spaetere Lesezugriff warf. Ein unerwarteter Antwortkoerper darf den
           Cache nicht vergiften; lieber den alten Stand behalten. */
        if (!data || typeof data.settings !== 'object' || data.settings === null) {
            console.warn('/settings returned no settings object — keeping the previous cache.');
            return;
        }
        localStorage.setItem('modus_settings_cache', JSON.stringify(data.settings));
    },

    saveSystemSettings: async (settings: SystemSettings) => {
        await fetchWithAuth(`${API_BASE_URL}/settings`, {
            method: 'PUT',
            body: JSON.stringify({ settings })
        });
        localStorage.setItem('modus_settings_cache', JSON.stringify(settings));
    },

    // Misc
    trackPresence: (() => {
        // Debounce: max 1 call per 30s per tab. Coalesce rapid nav into latest page.
        // Reason: was firing on every SPA route change -> hammered DB pool.
        let lastSent = 0;
        let pendingTimer: ReturnType<typeof setTimeout> | null = null;
        let pendingView = '';
        let pendingUser: User | null = null;
        const MIN_INTERVAL_MS = 30000;

        const send = () => {
            lastSent = Date.now();
            pendingTimer = null;
            const view = pendingView;
            pendingView = '';
            pendingUser = null;
            const sessionToken = localStorage.getItem('modus_session_token');
            fetchWithAuth(`${API_BASE_URL}/users/presence`, {
                method: 'POST',
                body: JSON.stringify({
                    session_token: sessionToken,
                    current_page: view,
                    user_agent: navigator.userAgent,
                    device: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
                })
            }).then(res => res.json()).then(data => {
                if (data.session_token) {
                    localStorage.setItem('modus_session_token', data.session_token);
                }
            }).catch(() => { });
        };

        return (view: string, user: User) => {
            pendingView = view;
            pendingUser = user;
            const elapsed = Date.now() - lastSent;
            if (elapsed >= MIN_INTERVAL_MS) {
                if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }
                send();
            } else if (!pendingTimer) {
                pendingTimer = setTimeout(send, MIN_INTERVAL_MS - elapsed);
            }
        };
    })(),

    getTicketById: (id: string): Ticket | undefined => {
        const tickets = db.getTickets();
        return tickets.find(t => t.id === id);
    },

    replyToTicket: async (ticketId: string, text: string, sender: 'user' | 'admin' | 'guest', attachments?: string[], isInternalNote?: boolean, guestId?: string) => {
        const payload = {
            text,
            sender,
            attachments: attachments || [],
            is_internal_note: isInternalNote || false,
            guest_id: guestId
        };

        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}/reply`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            console.error("Failed to reply to ticket");
            throw new Error("Failed to send reply");
        }

        await db.syncTickets();
        return await response.json();
    },

    handoffTicket: async (ticketId: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}/handoff`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error("Failed to hand off ticket");
        await db.syncTickets();
        return await response.json();
    },

    submitCsatRating: async (ticketId: string, rating: number) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}/csat?rating=${rating}`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error("Failed to submit CSAT rating");
        await db.syncTickets();
        return await response.json();
    },

    markTicketRead: async (ticketId: string) => {
        const guestId = !getStorageItem('tgp_token') ? getStorageItem('modus_guest_id') : null;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}/read`, {
                method: 'POST',
                body: JSON.stringify(guestId ? { guest_id: guestId } : {}),
            });
            if (response.ok) {
                await db.syncTickets();
                return;
            }
        } catch {
            // Fall through to local cache update as an offline fallback.
        }
        const tickets = db.getTickets();
        const ticket = tickets.find(t => t.id === ticketId);
        if (ticket) {
            ticket.unread = false;
            localStorage.setItem('modus_tickets_cache', JSON.stringify(tickets));
        }
    },

    updateTicketStatus: async (ticketId: string, status: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}`, {
            method: 'PUT',
            body: JSON.stringify({ status })
        });
        if (!response.ok) throw new Error("Failed to update ticket status");
        await db.syncTickets();
    },

    updateTicketPriority: async (ticketId: string, priority: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}`, {
            method: 'PUT',
            body: JSON.stringify({ priority })
        });
        if (!response.ok) throw new Error("Failed to update ticket priority");
        await db.syncTickets();
    },

    deleteTicket: async (ticketId: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error("Failed to delete ticket");
        await db.syncTickets();
    },

    bulkUpdateTickets: async (ticketIds: string[], action: 'close' | 'delete' | 'open' | 'archive') => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/bulk-action`, {
            method: 'PUT',
            body: JSON.stringify({ ticket_ids: ticketIds, action })
        });
        if (!response.ok) throw new Error("Failed to perform bulk action");
        await db.syncTickets();
    },

    assignTicket: async (ticketId: string, assigneeId: string | null) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}/assign?assignee_id=${assigneeId || ''}`, {
            method: 'PUT'
        });
        if (!response.ok) throw new Error("Failed to assign ticket");
        await db.syncTickets();
    },

    updateTicketTags: async (ticketId: string, tags: string[]) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/${ticketId}`, {
            method: 'PUT',
            body: JSON.stringify({ tags })
        });
        if (!response.ok) throw new Error("Failed to update ticket tags");
        await db.syncTickets();
    },

    getTicketTemplates: async () => {
        const response = await fetchWithAuth(`${API_BASE_URL}/ticket-templates`);
        if (!response.ok) return [];
        return await response.json();
    },

    createTicketTemplate: async (template: { title: string; content: string; category: string; shortcut?: string }) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/ticket-templates`, {
            method: 'POST',
            body: JSON.stringify({ ...template, is_active: true })
        });
        if (!response.ok) throw new Error("Failed to create template");
        return await response.json();
    },

    deleteTicketTemplate: async (templateId: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/ticket-templates/${templateId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error("Failed to delete template");
    },

    getProxyPoolStats: async (): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/pool/stats`);
        if (!response.ok) throw new Error('Failed to fetch proxy pool stats');
        return await response.json();
    },

    getDetailedBandwidthStats: async (): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/bandwidth/detailed`);
        if (!response.ok) throw new Error('Failed to fetch detailed bandwidth stats');
        return await response.json();
    },

    triggerProxyPoolSync: async (countryCode?: string): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/pool/sync${countryCode ? `?country_code=${countryCode}` : ''}`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to trigger proxy pool sync');
        return await response.json();
    },

    getUserStatsById: async (userId: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${userId}/stats`);
        if (!response.ok) throw new Error("Failed to get user stats");
        return await response.json();
    },

    savePricing: async (pricing: PriceClass[]) => {
        const settings = db.getSystemSettings();
        settings.pricingPlans = pricing;
        await db.saveSystemSettings(settings);
    },

    updateUserProfile: async (user: User) => {
        console.log("Updating profile on backend:", user);

        // Map camelCase to snake_case for backend
        const updatePayload = {
            name: user.name,
            email: user.email,
            phone: user.phone,
            company: user.company,
            vat_id: user.vatId,
            address: user.address,
            city: user.city,
            country: user.country,
            zip: user.zip,
            website: user.website,
            display_name: user.displayName,
            bio: user.bio,
            job_title: user.jobTitle,
            public_profile: user.publicProfile,
            two_factor_enabled: user.twoFactorEnabled,
            email_frequency: user.emailFrequency,
            login_notification_enabled: user.loginNotificationEnabled,
            newsletter_sub: user.newsletterSub,
            sound_effects: user.soundEffects,
            developer_mode: user.developerMode,
            api_whitelist: user.apiWhitelist,
            webhook_secret: user.webhookSecret,
            accessibility: user.accessibility,
            social_links: user.socialLinks,
            recovery_email: user.recoveryEmail,
            timezone: user.timezone,
            language: user.language,
            theme_accent_color: user.themeAccentColor,
            date_format: user.dateFormat,
            number_format: user.numberFormat,
            require_password_reset: user.requirePasswordReset
        };

        const response = await fetchWithAuth(`${API_BASE_URL}/users/me`, {
            method: 'PUT',
            body: JSON.stringify(updatePayload)
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || "Failed to update profile");
        }

        const updatedUser = await response.json();

        // Map the response back to frontend User type and update localStorage
        const mappedUser: User = {
            id: updatedUser.id,
            email: updatedUser.email,
            name: updatedUser.name || updatedUser.email?.split('@')[0] || 'User',
            role: updatedUser.role,
            balance: updatedUser.balance,
            balanceEconomy: updatedUser.balance_economy,
            balanceProfessional: updatedUser.balance_professional,
            balanceExpert: updatedUser.balance_expert,
            balanceSeo: updatedUser.balance_seo,
            status: updatedUser.status || 'active',
            joinedDate: updatedUser.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
            projectsCount: updatedUser.projects_count || 0,
            apiKey: updatedUser.api_key,
            isVerified: updatedUser.is_verified ?? false,
            isIdentityVerified: updatedUser.is_identity_verified ?? updatedUser.is_verified ?? false,
            phone: updatedUser.phone,
            phoneVerified: updatedUser.phone_verified,
            phoneVerifiedAt: updatedUser.phone_verified_at,
            phoneVerificationProvider: updatedUser.phone_verification_provider,
            phoneVerificationChannel: updatedUser.phone_verification_channel,
            company: updatedUser.company,
            vatId: updatedUser.vat_id,
            address: updatedUser.address,
            city: updatedUser.city,
            country: updatedUser.country,
            zip: updatedUser.zip,
            website: updatedUser.website,
            displayName: updatedUser.display_name,
            bio: updatedUser.bio,
            jobTitle: updatedUser.job_title,
            publicProfile: updatedUser.public_profile,
            twoFactorEnabled: updatedUser.two_factor_enabled,
            emailFrequency: updatedUser.email_frequency,
            loginNotificationEnabled: updatedUser.login_notification_enabled,
            newsletterSub: updatedUser.newsletter_sub,
            soundEffects: updatedUser.sound_effects,
            developerMode: updatedUser.developer_mode,
            apiWhitelist: updatedUser.api_whitelist,
            webhookSecret: updatedUser.webhook_secret,
            accessibility: updatedUser.accessibility,
            socialLinks: updatedUser.social_links,
            recoveryEmail: updatedUser.recovery_email,
            timezone: updatedUser.timezone,
            language: updatedUser.language,
            themeAccentColor: updatedUser.theme_accent_color,
            dateFormat: updatedUser.date_format,
            numberFormat: updatedUser.number_format,
            requirePasswordReset: updatedUser.require_password_reset,
            avatarUrl: updatedUser.avatar_url,
        };

        db.setCurrentUser(mappedUser);
        return mappedUser;
    },

    regenerateApiKey: async (): Promise<string> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/auth/api-key`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error("Failed to regenerate key");
        const data = await response.json();

        const user = db.getCurrentUser();
        if (user) {
            user.apiKey = data.api_key;
            db.setCurrentUser(user);
        }
        return data.api_key;
    },

    revokeApiKey: async (): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/auth/api-key`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error("Failed to revoke key");

        const user = db.getCurrentUser();
        if (user) {
            user.apiKey = undefined;
            db.setCurrentUser(user);
        }
    },

    changePassword: async (current: string, newPass: string, confirm: string) => {
        const response = await fetchWithAuth(`${API_BASE_URL}/auth/password`, {
            method: 'PUT',
            body: JSON.stringify({ current_password: current, new_password: newPass, confirm_password: confirm })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || "Password update failed");
        }
        return await response.json();
    },

    logoutAllSessions: async () => {
        const response = await fetchWithAuth(`${API_BASE_URL}/auth/logout-all`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error("Failed to logout all sessions");
        return await response.json();
    },

    exportUserData: async () => {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/me/export`);
        if (!response.ok) throw new Error("Export failed");
        return await response.json();
    },

    exportMyTickets: async () => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/my-export`);
        if (!response.ok) throw new Error("Ticket export failed");
        return await response.json();
    },

    deleteMyTicketData: async (): Promise<{ deleted_tickets: number }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets/my-data`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error("Failed to delete ticket history");
        const result = await response.json();
        // Drop the now-stale local ticket cache, same as deleteTicket().
        await db.syncTickets();
        return result;
    },

    uploadAvatar: async (file: File): Promise<string> => {
        const token = localStorage.getItem('tgp_token');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE_URL}/users/me/avatar`, {
            method: 'POST',
            headers: headers,
            body: formData
        });

        if (!response.ok) throw new Error("Avatar upload failed");

        const data = await response.json();
        const user = db.getCurrentUser();
        if (user) {
            user.avatarUrl = data.avatar_url;
            db.setCurrentUser(user);
        }
        return data.avatar_url;
    },

    deleteAccount: async (): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/me`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error("Account deletion failed");
        db.logout();
    },

    getPricing: (): PriceClass[] => {
        const settings = db.getSystemSettings();
        if (settings.pricingPlans && settings.pricingPlans.length > 0) {
            return settings.pricingPlans;
        }
        return [
            { id: 'foundation', name: 'Foundation', hourlyRate: 0.58, baseFee: 29, examFee: 0 },
            { id: 'momentum', name: 'Momentum', hourlyRate: 0.43, baseFee: 129, examFee: 0 },
            { id: 'breakthrough', name: 'Breakthrough', hourlyRate: 0.40, baseFee: 399, examFee: 0 },
            { id: 'apex', name: 'Apex', hourlyRate: 0.33, baseFee: 999, examFee: 0 },
        ];
    },

    getAlerts: (): SystemAlert[] => {
        // This is synchronous in current design but backend is async.
        // We rely on syncAlerts (below) to populate cache or we make this async?
        // The component calls it synchronously: setAlerts(db.getAlerts())
        // So we must use cache.
        const data = localStorage.getItem('modus_alerts_cache');
        return data ? JSON.parse(data) : [];
    },

    syncAlerts: async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts`);
            if (!response.ok) return;
            const data = await response.json();
            const mapped: SystemAlert[] = data.map((b: any) => ({
                id: b.id,
                message: b.message,
                type: b.type,
                active: b.is_active,
                date: b.created_at,
                targetType: 'all', // Backend doesn't support this yet
                title: b.title // Extended SystemAlert to include title? It works if interface ignores it or we extend it.
            }));
            localStorage.setItem('modus_alerts_cache', JSON.stringify(mapped));
        } catch (e) {
            console.error("Failed to sync alerts:", e);
        }
    },

    checkBroadcastTarget: (alert: SystemAlert, user?: User): boolean => {
        if (!alert.active) return false;
        if (!user) return alert.targetType === 'all';

        switch (alert.targetType) {
            case 'all': return true;
            case 'paying': return user.projectsCount > 0;
            case 'active_7d':
                const joinTime = new Date(user.joinedDate).getTime();
                const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
                return (Date.now() - joinTime) > sevenDaysMs;
            case 'specific':
                return alert.targetUserIds ? alert.targetUserIds.includes(user.id) : false;
            default: return false;
        }
    },

    getRealTimeVisitors: async (): Promise<LiveVisitor[]> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/active-users`);
            if (!response.ok) return [];
            const data = await response.json();
            return data.map((s: any) => ({
                id: s.id,
                userId: s.user?.id,
                name: s.user?.name || 'Guest',
                email: s.user?.email || '',
                role: s.role,
                currentPage: s.currentPage,
                durationMinutes: s.durationMinutes,
                device: s.device,
                browser: s.browser,
                ip: s.ip,
                location: s.location,
                totalVisits: s.totalVisits,
                status: s.status,
                lastActive: s.lastActive
            }));
        } catch (e) {
            console.error("Failed to fetch real-time visitors:", e);
            return [];
        }
    },

    scanGA4: async (url: string): Promise<string> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tools/scan-ga4?url=${encodeURIComponent(url)}`);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'GA4 ID not found');
        }
        const data = await response.json();
        return data.tid;
    },

    validateSitemap: async (url: string): Promise<{ valid: boolean; format: string; url_count: number; message: string; preview: string[] }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tools/validate-sitemap?url=${encodeURIComponent(url)}`);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Sitemap validation failed');
        }
        return response.json();
    },

    spendCredits: (amount: number, description: string): boolean => {
        const user = db.getCurrentUser();
        if (!user || user.balance < amount) return false;

        // Optimistic update
        user.balance -= amount;
        db.setCurrentUser(user);

        // Async sync with backend (fire and forget)
        db.purchaseCredits(-amount, description).catch(console.error);

        return true;
    },

    // Broadcasts
    getActiveBroadcasts: async (): Promise<Broadcast[]> => {
        try {
            const response = await fetch(`${API_BASE_URL}/broadcasts/active`);
            if (!response.ok) return [];
            const data = await response.json();
            return data.map((b: any) => ({
                id: b.id,
                title: b.title,
                message: b.message,
                type: b.type,
                isActive: b.is_active,
                createdAt: b.created_at,
                expiresAt: b.expires_at,
                actionUrl: b.action_url,
                actionText: b.action_text
            }));
        } catch (e) {
            console.error("Failed to fetch active broadcasts", e);
            return [];
        }
    },

    getAdminBroadcasts: async (): Promise<Broadcast[]> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts`);
            if (!response.ok) return [];
            const data = await response.json();
            return data.map((b: any) => ({
                id: b.id,
                title: b.title,
                message: b.message,
                type: b.type,
                isActive: b.is_active,
                createdAt: b.created_at,
                expiresAt: b.expires_at,
                actionUrl: b.action_url,
                actionText: b.action_text
            }));
        } catch (e) {
            console.error("Failed to fetch admin broadcasts", e);
            return [];
        }
    },

    createBroadcast: async (broadcast: Partial<Broadcast>) => {
        const payload = {
            title: broadcast.title,
            message: broadcast.message,
            type: broadcast.type,
            is_active: broadcast.isActive,
            expires_at: broadcast.expiresAt ? new Date(broadcast.expiresAt).toISOString() : null,
            action_url: broadcast.actionUrl,
            action_text: broadcast.actionText
        };
        await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },

    updateBroadcast: async (id: string, broadcast: Partial<Broadcast>) => {
        const payload = {
            title: broadcast.title,
            message: broadcast.message,
            type: broadcast.type,
            is_active: broadcast.isActive,
            expires_at: broadcast.expiresAt ? new Date(broadcast.expiresAt).toISOString() : null,
            action_url: broadcast.actionUrl,
            action_text: broadcast.actionText
        };
        await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    },

    deleteBroadcast: async (id: string) => {
        await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts/${id}`, {
            method: 'DELETE'
        });
    },

    getAdminStats: async (): Promise<AdminStats | null> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/stats`);
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch admin stats", e);
            return null;
        }
    },

    streamAdminLiveTraffic: async (
        params: {
            limit?: number;
            offset?: number;
            status?: string;
            search?: string;
            sort?: string;
            direction?: 'asc' | 'desc';
        },
        handlers: {
            onSnapshot: (snapshot: AdminLiveTrafficSnapshot) => void;
            onError?: (error: Error) => void;
            signal?: AbortSignal;
        }
    ): Promise<void> => {
        const query = new URLSearchParams();
        query.set('limit', String(params.limit ?? 50));
        query.set('offset', String(params.offset ?? 0));
        query.set('status', params.status || 'all');
        query.set('sort', params.sort || 'hitsToday');
        query.set('direction', params.direction || 'desc');
        if (params.search?.trim()) query.set('search', params.search.trim());

        const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

        while (!handlers.signal?.aborted) {
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/admin/live-traffic-snapshot?${query.toString()}`, {
                    signal: handlers.signal,
                });
                if (!response.ok) {
                    throw new Error(await readApiError(response, 'Failed to load live traffic snapshot'));
                }
                handlers.onSnapshot(await response.json() as AdminLiveTrafficSnapshot);
            } catch (error) {
                if ((error as Error).name === 'AbortError' || handlers.signal?.aborted) return;
                handlers.onError?.(error instanceof Error ? error : new Error('Live traffic refresh failed'));
            }
            await wait(2000);
        }
    },

    getAdminProjectTrafficHistory: async (projectId: string, days: number = 30): Promise<ProjectTrafficHistoryResponse> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/${projectId}/traffic-history?days=${days}`);
        if (!response.ok) throw new Error(await readApiError(response, 'Failed to fetch project traffic history'));
        return response.json();
    },

    getMonitoringData: async (): Promise<any | null> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/monitoring`);
            if (!response.ok) return null;
            return await response.json();
        } catch (e) {
            console.error("Failed to fetch monitoring data", e);
            return null;
        }
    },

    // Marketing & Coupons
    getCoupons: async (): Promise<Coupon[]> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/coupons`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            return data.map((c: any) => ({
                id: c.id,
                code: c.code,
                discountType: c.discount_type,
                discountValue: c.discount_value,
                minPurchase: c.min_purchase,
                maxUses: c.max_uses,
                usedCount: c.used_count,
                maxUsesPerUser: c.max_uses_per_user,
                planRestriction: c.plan_restriction,
                duration: c.duration,
                expiryDate: c.expires_at ? c.expires_at.split('T')[0] : null,
                active: c.is_active
            }));
        } catch (e) {
            return [];
        }
    },

    saveCoupon: async (coupon: Coupon): Promise<void> => {
        const payload = {
            code: coupon.code,
            discount_type: coupon.discountType === 'percent' ? 'percentage' : 'fixed',
            discount_value: coupon.discountValue,
            min_purchase: coupon.minPurchase || 0,
            max_uses: coupon.maxUses,
            max_uses_per_user: coupon.maxUsesPerUser || 1,
            plan_restriction: coupon.planRestriction,
            duration: coupon.duration || 'once',
            expires_at: coupon.expiryDate || null
        };

        if (coupon.id) {
            await fetchWithAuth(`${API_BASE_URL}/admin/coupons/${coupon.id}`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });
        } else {
            await fetchWithAuth(`${API_BASE_URL}/admin/coupons`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
        }
    },

    deleteCoupon: async (id: string): Promise<void> => {
        await fetchWithAuth(`${API_BASE_URL}/admin/coupons/${id}`, {
            method: 'DELETE'
        });
    },

    getMarketingStats: async (): Promise<MarketingCampaign[]> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/admin/marketing`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            return data.map((c: any) => ({
                id: c.id,
                name: c.name,
                type: c.campaign_type,
                status: c.status,
                clicks: c.clicks,
                conversions: c.conversions,
                revenue: c.revenue,
                spend: c.spend,
                dateCreated: c.created_at ? c.created_at.split('T')[0] : new Date().toISOString().split('T')[0]
            }));
        } catch (e) {
            return [];
        }
    },

    createMarketingCampaign: async (campaign: Partial<MarketingCampaign>): Promise<void> => {
        await fetchWithAuth(`${API_BASE_URL}/admin/marketing`, {
            method: 'POST',
            body: JSON.stringify({
                name: campaign.name,
                campaign_type: campaign.type || 'ad_tracking',
                start_date: campaign.dateCreated || null,
                end_date: null
            })
        });
    },

    sendEmailBlast: async (subject: string, body: string, targetAudience: string = 'all'): Promise<void> => {
        await fetchWithAuth(`${API_BASE_URL}/admin/email-blast`, {
            method: 'POST',
            body: JSON.stringify({
                subject,
                body,
                target_audience: targetAudience
            })
        });
    },

    // Conversion Settings
    getConversionSettings: async (): Promise<ConversionSettings> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/conversion-settings`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            return {
                socialProof: {
                    enabled: data.socialProofEnabled,
                    position: data.socialProofPosition,
                    delay: data.socialProofDelay,
                    showRealData: data.socialProofShowSimulated,
                    customMessages: []
                },
                exitIntent: {
                    enabled: data.exitIntentEnabled,
                    headline: data.exitIntentHeadline,
                    subtext: data.exitIntentSubtext,
                    couponCode: data.exitIntentCouponCode,
                    showOncePerSession: true
                },
                promoBar: {
                    enabled: data.promoBarEnabled,
                    message: data.promoBarMessage,
                    buttonText: data.promoBarButtonText,
                    buttonLink: data.promoBarButtonUrl,
                    backgroundColor: data.promoBarBackgroundColor,
                    textColor: data.promoBarTextColor
                }
            };
        } catch (e) {
            const initial: ConversionSettings = {
                socialProof: { enabled: false, position: 'bottom-left', delay: 5, showRealData: false, customMessages: [] },
                exitIntent: { enabled: false, headline: 'Wait! Don\'t miss out.', subtext: 'Get 20% off with code: STAY20', couponCode: 'STAY20', showOncePerSession: true },
                promoBar: { enabled: false, message: 'Limited Time Offer!', buttonText: 'Claim Offer', buttonLink: '/pricing', backgroundColor: '#ff5a0a', textColor: '#ffffff' }
            };
            return initial;
        }
    },

    saveConversionSettings: async (settings: ConversionSettings): Promise<void> => {
        await fetchWithAuth(`${API_BASE_URL}/conversion-settings`, {
            method: 'PUT',
            body: JSON.stringify({
                social_proof_enabled: settings.socialProof.enabled,
                social_proof_position: settings.socialProof.position,
                social_proof_delay: settings.socialProof.delay,
                social_proof_show_simulated: settings.socialProof.showRealData,
                exit_intent_enabled: settings.exitIntent.enabled,
                exit_intent_headline: settings.exitIntent.headline,
                exit_intent_subtext: settings.exitIntent.subtext,
                exit_intent_coupon_code: settings.exitIntent.couponCode,
                promo_bar_enabled: settings.promoBar.enabled,
                promo_bar_message: settings.promoBar.message,
                promo_bar_button_text: settings.promoBar.buttonText,
                promo_bar_button_url: settings.promoBar.buttonLink,
                promo_bar_background_color: settings.promoBar.backgroundColor,
                promo_bar_text_color: settings.promoBar.textColor
            })
        });
    },

    // Loyalty & Referrals
    getLoyaltySettings: async (): Promise<import('../types').LoyaltySettings> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/loyalty-settings`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            return { enabled: data.enabled, pointsPerDollar: data.pointsPerDollar, redemptionRate: data.redemptionRate, bonusSignupPoints: data.bonusSignupPoints };
        } catch (e) {
            return { enabled: false, pointsPerDollar: 1, redemptionRate: 100, bonusSignupPoints: 50 };
        }
    },

    saveLoyaltySettings: async (settings: import('../types').LoyaltySettings): Promise<void> => {
        await fetchWithAuth(`${API_BASE_URL}/loyalty-settings`, {
            method: 'PUT',
            body: JSON.stringify(settings)
        });
    },

    getReferralSettings: async (): Promise<import('../types').ReferralSettings> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/referral-settings`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            return { enabled: data.enabled, referrerReward: data.referrerRewardValue, refereeReward: data.refereeRewardValue, rewardType: data.referrerRewardType as 'credit' | 'percentage' };
        } catch (e) {
            return { enabled: false, referrerReward: 25, refereeReward: 25, rewardType: 'credit' };
        }
    },

    saveReferralSettings: async (settings: import('../types').ReferralSettings): Promise<void> => {
        await fetchWithAuth(`${API_BASE_URL}/referral-settings`, {
            method: 'PUT',
            body: JSON.stringify({
                enabled: settings.enabled,
                referrer_reward_type: settings.rewardType,
                referrer_reward_value: settings.referrerReward,
                referee_reward_type: settings.rewardType,
                referee_reward_value: settings.refereeReward
            })
        });
    },

    getBankTransfers: async (statusFilter: string = 'all'): Promise<any[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/bank-transfers?status_filter=${statusFilter}`);
        if (!response.ok) return [];
        return await response.json();
    },

    getMyBankTransfers: async (): Promise<any[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/bank-transfer/my-proofs`);
        if (!response.ok) return [];
        return await response.json();
    },

    getFAQs: async (): Promise<any[]> => {
        try {
            const response = await fetch(`${API_BASE_URL}/faqs`);
            if (!response.ok) return [];
            return await response.json();
        } catch (e) {
            return [];
        }
    },

    approveBankTransfer: async (proofId: string, approved: boolean, adminNotes?: string): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/bank-transfers/${proofId}/approve`, {
            method: 'PUT',
            body: JSON.stringify({ approved, admin_notes: adminNotes })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || "Failed to process transfer");
        }
        return await response.json();
    },

    updateTransaction: async (trx: Transaction): Promise<Transaction> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/transactions/${trx.id}`, {
            method: 'PUT',
            body: JSON.stringify({
                description: trx.desc,
                amount: trx.amount,
                type: trx.type,
                status: trx.status,
                tier: trx.tier,
                reference: trx.reference
            })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || "Failed to update transaction");
        }
        const updated = await response.json();
        await db.syncAllTransactions();
        return {
            id: updated.id,
            date: updated.created_at?.split('T')[0] || trx.date,
            description: updated.description || trx.desc || '',
            desc: updated.description || trx.desc,
            amount: updated.amount,
            status: updated.status,
            type: updated.type,
            user_id: updated.user_id,
            userId: updated.user_id,
            tier: updated.tier,
            reference: updated.reference
        };
    },

    // Admin User Details
    getUserDetails: async (userId: string): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/details`);
        if (!response.ok) throw new Error('Failed to fetch user details');
        const data = await response.json();

        return {
            user: {
                id: data.user.id,
                email: data.user.email,
                /* Fehlte in der Zuordnung, obwohl das Backend es liefert
                   (routers/admin_users.py:209 `name=user.name or user.display_name`).
                   Folge: die Detailseite zeigte fuer JEDES Konto "Unnamed user"
                   und trug die E-Mail als Ueberschrift — auch bei Nutzern mit
                   hinterlegtem Namen. */
                name: data.user.name,
                role: data.user.role,
                balance: data.user.balance,
                balanceEconomy: data.user.balance_economy,
                balanceProfessional: data.user.balance_professional,
                balanceExpert: data.user.balance_expert,
                balanceSeo: data.user.balance_seo,
                status: data.user.status,
                plan: data.user.plan,
                isVerified: data.user.is_verified,
                isIdentityVerified: data.user.is_identity_verified ?? data.user.is_verified,
                shadowBanned: data.user.shadow_banned,
                notes: data.user.notes,
                tags: data.user.tags || [],
                banReason: data.user.ban_reason,
                lastIp: data.user.last_ip,
                joinedDate: data.user.created_at?.split('T')[0]
            },
            tierBalances: data.tier_balances,
            totalSpent: data.total_spent,
            totalHitsPurchased: data.total_hits_purchased,
            totalHitsUsed: data.total_hits_used,
            transactionsCount: data.transactions_count,
            projectsCount: data.projects_count,
            ticketsCount: data.tickets_count,
            referralsCount: data.referrals_count,
            referralEarnings: data.referral_earnings,
            notificationPrefs: data.notification_prefs
        };
    },

    getUserActivity: async (userId: string, limit = 50, offset = 0, actionType?: string): Promise<ActivityLog[]> => {
        let url = `${API_BASE_URL}/admin/users/${userId}/activity?limit=${limit}&offset=${offset}`;
        if (actionType) url += `&action_type=${actionType}`;

        const response = await fetchWithAuth(url);
        if (!response.ok) throw new Error('Failed to fetch activity');
        const data = await response.json();

        return data.map((a: any) => ({
            id: a.id,
            userId: a.user_id,
            actionType: a.action_type,
            actionDetail: a.action_detail,
            ipAddress: a.ip_address,
            userAgent: a.user_agent,
            createdAt: a.created_at
        }));
    },

    getUserSessions: async (userId: string): Promise<UserSession[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/sessions`);
        if (!response.ok) throw new Error('Failed to fetch sessions');
        const data = await response.json();

        return data.map((s: any) => ({
            id: s.id,
            userId: s.user_id,
            ipAddress: s.ip_address,
            userAgent: s.user_agent,
            deviceInfo: s.device_info,
            location: s.location,
            createdAt: s.created_at,
            lastActivity: s.last_activity,
            expiresAt: s.expires_at,
            isActive: s.is_active
        }));
    },

    terminateUserSession: async (userId: string, sessionId: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/sessions/${sessionId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to terminate session');
    },

    getImpersonationLog: async (userId: string): Promise<ImpersonationLog[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/impersonation-log`);
        if (!response.ok) throw new Error('Failed to fetch impersonation log');
        const data = await response.json();

        return data.map((l: any) => ({
            id: l.id,
            adminId: l.admin_id,
            targetUserId: l.target_user_id,
            action: l.action,
            ipAddress: l.ip_address,
            createdAt: l.created_at,
            adminEmail: l.admin_email
        }));
    },

    getBalanceAdjustments: async (userId: string): Promise<BalanceAdjustmentLog[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/balance-adjustments`);
        if (!response.ok) throw new Error('Failed to fetch balance adjustments');
        const data = await response.json();

        return data.map((a: any) => ({
            id: a.id,
            userId: a.user_id,
            adminId: a.admin_id,
            adjustmentType: a.adjustment_type,
            tier: a.tier,
            amount: a.amount,
            hits: a.hits,
            reason: a.reason,
            notes: a.notes,
            createdAt: a.created_at,
            adminEmail: a.admin_email
        }));
    },

    adjustUserBalance: async (userId: string, adjustment: {
        adjustmentType: string;
        tier: string;
        amount: number;
        hits?: number;
        reason: string;
        notes?: string;
    }): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/adjust-balance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                adjustment_type: adjustment.adjustmentType,
                tier: adjustment.tier,
                amount: adjustment.amount,
                hits: adjustment.hits,
                reason: adjustment.reason,
                notes: adjustment.notes
            })
        });
        if (!response.ok) {
            let errorMsg = 'Failed to adjust balance';
            try {
                const errorData = await response.json();
                errorMsg = errorData.detail || errorMsg;
            } catch { }
            throw new Error(errorMsg);
        }
    },

    addBonusHits: async (userId: string, request: {
        tier: string;
        hits: number;
        reason: string;
    }): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/add-bonus-hits`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request)
        });
        if (!response.ok) {
            let errorMsg = 'Failed to add bonus hits';
            try {
                const errorData = await response.json();
                errorMsg = errorData.detail || errorMsg;
            } catch { }
            throw new Error(errorMsg);
        }
    },

    getUserEmails: async (userId: string, limit = 50): Promise<EmailLog[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/emails?limit=${limit}`);
        if (!response.ok) throw new Error('Failed to fetch emails');
        const data = await response.json();

        return data.map((e: any) => ({
            id: e.id,
            userId: e.user_id,
            emailType: e.email_type,
            toEmail: e.to_email,
            subject: e.subject,
            status: e.status,
            errorMessage: e.error_message,
            sentAt: e.sent_at,
            deliveredAt: e.delivered_at
        }));
    },

    getUserReferrals: async (userId: string): Promise<{ referrals: UserReferral[]; totalReferrals: number; totalEarnings: number }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/referrals`);
        if (!response.ok) throw new Error('Failed to fetch referrals');
        const data = await response.json();

        return {
            referrals: (data.referrals || []).map((r: any) => ({
                id: r.id,
                email: r.email,
                name: r.name,
                status: r.status,
                createdAt: r.created_at,
                totalSpent: r.total_spent,
                earningsFromRef: r.earnings_from_ref
            })),
            totalReferrals: data.total_referrals,
            totalEarnings: data.total_earnings
        };
    },

    getUserNotificationPrefs: async (userId: string): Promise<UserNotificationPrefs> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/notification-prefs`);
        if (!response.ok) throw new Error('Failed to fetch notification prefs');
        const data = await response.json();

        return {
            id: data.id,
            userId: data.user_id,
            emailMarketing: data.email_marketing,
            emailTransactional: data.email_transactional,
            emailAlerts: data.email_alerts,
            browserNotifications: data.browser_notifications,
            newsletterSub: data.newsletter_sub,
            emailFrequency: data.email_frequency,
            updatedAt: data.updated_at
        };
    },

    updateUserNotificationPrefs: async (userId: string, prefs: Partial<UserNotificationPrefs>): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/notification-prefs`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(prefs)
        });
        if (!response.ok) throw new Error('Failed to update notification prefs');
    },

    sendPasswordReset: async (userId: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/send-password-reset`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to send password reset');
    },

    resendVerification: async (userId: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/resend-verification`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to resend verification');
    },

    adminRegenerateApiKey: async (userId: string): Promise<string> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/regenerate-api-key`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to regenerate API key');
        const data = await response.json();
        return data.api_key;
    },

    startImpersonation: async (userId: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/impersonate/${userId}`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to start impersonation');
        const data = await response.json();

        // 0. Backup Admin Token
        if (authToken) {
            localStorage.setItem('tgp_admin_token', authToken);
        }

        // 1. Set new token
        localStorage.setItem('tgp_token', data.token);
        authToken = data.token; // Update module-scope variable

        // 2. Fetch User Details with new token
        const userRes = await fetchWithAuth(`${API_BASE_URL}/users/me`);
        if (!userRes.ok) throw new Error('Failed to fetch impersonated user details');
        const userData = await userRes.json();

        // 3. Map and Set Current User
        const mappedUser: User = {
            id: userData.id,
            email: userData.email,
            name: (userData.name || userData.email.split('@')[0]),
            role: userData.role,
            balance: userData.balance,
            balanceEconomy: userData.balance_economy,
            balanceProfessional: userData.balance_professional,
            balanceExpert: userData.balance_expert,
            balanceSeo: userData.balance_seo,
            status: userData.status,
            plan: userData.plan,
            isVerified: userData.is_verified,
            isIdentityVerified: userData.is_identity_verified ?? userData.is_verified,
            shadowBanned: userData.shadow_banned,
            notes: userData.notes,
            tags: userData.tags,
            banReason: userData.ban_reason,
            createdAt: userData.created_at,
            joinedDate: userData.created_at || new Date().toISOString(),
            projectsCount: 0,
            lastIp: userData.last_ip,
            lastActive: userData.last_active,

            // Extended Profile
            phone: userData.phone,
            phoneVerified: userData.phone_verified,
            phoneVerifiedAt: userData.phone_verified_at,
            phoneVerificationProvider: userData.phone_verification_provider,
            phoneVerificationChannel: userData.phone_verification_channel,
            company: userData.company,
            vatId: userData.vat_id,
            address: userData.address,
            city: userData.city,
            country: userData.country,
            zip: userData.zip,
            website: userData.website,
            displayName: userData.display_name,
            bio: userData.bio,
            jobTitle: userData.job_title,
            publicProfile: userData.public_profile,
            twoFactorEnabled: userData.two_factor_enabled,
            emailFrequency: userData.email_frequency,
            loginNotificationEnabled: userData.login_notification_enabled,
            newsletterSub: userData.newsletter_sub,
            soundEffects: userData.sound_effects,
            developerMode: userData.developer_mode,
            apiWhitelist: userData.api_whitelist,
            webhookSecret: userData.webhook_secret,
            accessibility: userData.accessibility,
            socialLinks: userData.social_links,
            recoveryEmail: userData.recovery_email,
            timezone: userData.timezone,
            language: userData.language,
            themeAccentColor: userData.theme_accent_color,
            dateFormat: userData.date_format,
            numberFormat: userData.number_format,
            requirePasswordReset: userData.require_password_reset
        };

        db.setCurrentUser(mappedUser);
    },

    stopImpersonation: async (): Promise<void> => {
        const adminToken = localStorage.getItem('tgp_admin_token');
        if (!adminToken) throw new Error('No admin session found');

        // 1. Restore Admin Token
        localStorage.setItem('tgp_token', adminToken);
        authToken = adminToken;
        localStorage.removeItem('tgp_admin_token');

        // 2. Fetch Admin User Details
        const userRes = await fetchWithAuth(`${API_BASE_URL}/users/me`);
        if (!userRes.ok) throw new Error('Failed to restore admin user details');
        const userData = await userRes.json();

        // 3. Map and Set Current User (can reuse login logic but duplicative here for now)
        const mappedUser: User = {
            id: userData.id,
            email: userData.email,
            name: (userData.name || userData.email.split('@')[0]),
            role: userData.role,
            balance: userData.balance,
            balanceEconomy: userData.balance_economy,
            balanceProfessional: userData.balance_professional,
            balanceExpert: userData.balance_expert,
            balanceSeo: userData.balance_seo,
            status: userData.status,
            plan: userData.plan,
            isVerified: userData.is_verified,
            isIdentityVerified: userData.is_identity_verified ?? userData.is_verified,
            shadowBanned: userData.shadow_banned,
            notes: userData.notes,
            tags: userData.tags,
            banReason: userData.ban_reason,
            createdAt: userData.created_at,
            joinedDate: userData.created_at || new Date().toISOString(),
            projectsCount: 0,
            lastIp: userData.last_ip,
            lastActive: userData.last_active,
            // ... minimal fields sufficient for redirect, complete mapping ideally
        };

        db.setCurrentUser(mappedUser);
    },

    adminExportUserData: async (userId: string): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/export`);
        if (!response.ok) throw new Error('Failed to export user data');
        return response.json();
    },

    // Get admin user transactions with hits (per-user endpoint)
    getUserTransactions: async (userId: string): Promise<Transaction[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/transactions`);
        if (!response.ok) return [];

        const data = await response.json();
        return data.map((t: any) => ({
            id: t.id,
            date: t.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
            desc: t.description || '',
            amount: t.amount,
            status: t.status || 'completed',
            type: t.type,
            userId: t.user_id,
            tier: t.tier,
            hits: t.hits,
            reference: t.reference
        }));
    },

    // Get admin user projects (per-user endpoint)
    getUserProjects: async (userId: string): Promise<Project[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}/projects`);
        if (!response.ok) return [];

        const data = await response.json();
        return data.map((p: any) => ({
            id: p.id,
            userId: p.user_id,
            name: p.name,
            plan: p.plan_type || 'Custom',
            tier: p.tier,
            status: p.status,
            customTarget: {
                totalVisitors: p.total_target || 0,
                durationDays: 30,
                dailyLimit: p.daily_limit || 0
            },
            expires: p.expires_at?.split('T')[0] || '',
            createdAt: p.created_at,
            settings: normalizeProjectUrlSettings(p.settings)
        }));
    },

    // Get admin user tickets
    getUserTickets: async (userId: string): Promise<Ticket[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/tickets`);
        if (!response.ok) return [];

        const data = await response.json();
        const userTickets = data.filter((t: any) => t.user_id === userId);

        return userTickets.map((t: any) => ({
            id: t.id,
            supportNumber: t.support_number,
            supportCode: t.support_code,
            type: t.type || 'ticket',
            userId: t.user_id,
            userName: '',
            subject: t.subject,
            status: t.status || 'open',
            priority: t.priority || 'low',
            source: t.source || 'user',
            date: t.created_at?.split('T')[0] || '',
            lastMessage: t.messages?.length > 0 ? t.messages[t.messages.length - 1].text : '',
            messages: t.messages || [],
            unread: false,
            csatRating: t.csat_rating
        }));
    },

    adminUpdateUser: async (userId: string, data: any): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/users/${userId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update user');
    },

    // Compatibility methods for AdminPanel.tsx
    updateUserStatus: async (userId: string, status: 'active' | 'suspended') => {
        await db.adminUpdateUser(userId, { status });
        // Return updated list for React state update simplicity (although inefficient)
        return db.getUsers();
    },

    adminAdjustBalance: async (userId: string, amount: number, type: 'credit' | 'debit', reason: string) => {
        await db.adjustUserBalance(userId, {
            adjustmentType: type,
            tier: 'general',
            amount: amount,
            reason: reason
        });
    },

    createAlert: async (message: string, type: 'info' | 'warning' | 'error', title: string = 'System Alert') => {
        const payload = {
            title: title,
            message: message,
            type: type,
            is_active: true
        };
        await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        await db.syncAlerts();
    },

    toggleAlert: async (id: string, active: boolean) => {
        // Backend endpoint for update is /admin/broadcasts/{id}
        // Need to fetch existing first or just patch?
        // Main.py has PUT /admin/broadcasts/{id} taking BroadcastCreate.
        // We need extended endpoint for PATCH or full update.
        // Assuming we can just update status?
        // Let's implement full update if needed, but for now we might fail if we don't send all fields.
        // Use a specialized endpoint if available, or fetch-then-update.
        // Logic:
        const alerts = db.getAlerts();
        const alert = alerts.find(a => a.id === id);
        if (!alert) return;

        const payload = {
            title: (alert as any).title || 'System Alert',
            message: alert.message,
            type: alert.type,
            is_active: active
        };

        await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
        await db.syncAlerts();
    },

    deleteAlert: async (id: string) => {
        // Backend API doesn't seem to have DELETE for broadcasts in main.py snippet I saw?
        // I saw GET, POST, PUT.
        // If missing, I need to add DELETE endpoint to main.py.
        // Assuming it exists or I will add it.
        await fetchWithAuth(`${API_BASE_URL}/admin/broadcasts/${id}`, {
            method: 'DELETE'
        });
        await db.syncAlerts();
    },

    // Benefits API
    getBenefitTypes: async (): Promise<import('../types').BenefitType[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/benefits/types`);
        if (!response.ok) return [];
        const data = await response.json();
        return data.map((b: any) => ({
            id: b.id,
            type: b.type,
            category: b.category,
            name: b.name,
            value: b.value,
            requirements: b.requirements,
            active: b.active,
            displayOrder: b.display_order
        }));
    },

    getMyBenefitRequests: async (status?: string): Promise<import('../types').BenefitRequest[]> => {
        const url = status ? `${API_BASE_URL}/benefits/my-requests?status=${status}` : `${API_BASE_URL}/benefits/my-requests`;
        const response = await fetchWithAuth(url);
        if (!response.ok) return [];
        const data = await response.json();
        return data.map((b: any) => ({
            id: b.id,
            userId: b.user_id,
            benefitType: b.benefit_type,
            benefitCategory: b.benefit_category,
            url: b.url,
            description: b.description,
            screenshotUrl: b.screenshot_url,
            claimedValue: b.claimed_value,
            approvedValue: b.approved_value,
            status: b.status,
            adminNotes: b.admin_notes,
            fraudFlagged: b.fraud_flagged,
            fraudReason: b.fraud_reason,
            submittedAt: b.submitted_at,
            reviewedAt: b.reviewed_at,
            reviewedBy: b.reviewed_by
        }));
    },

    getBenefitBalance: async (): Promise<import('../types').BenefitBalance> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/benefits/balance`);
        if (!response.ok) return { benefitBalance: 0, totalBenefitsClaimed: 0, pendingRequests: 0, approvedRequests: 0, rejectedRequests: 0 };
        const data = await response.json();
        return {
            benefitBalance: data.benefit_balance,
            totalBenefitsClaimed: data.total_benefits_claimed,
            pendingRequests: data.pending_requests,
            approvedRequests: data.approved_requests,
            rejectedRequests: data.rejected_requests
        };
    },

    submitBenefit: async (benefit: {
        benefit_type: string;
        benefit_category: string;
        url: string;
        description?: string;
        screenshot_url?: string;
        claimed_value: number;
    }): Promise<import('../types').BenefitRequest> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/benefits/submit`, {
            method: 'POST',
            body: JSON.stringify(benefit)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || "Failed to submit benefit");
        }
        const b = await response.json();
        return {
            id: b.id,
            userId: b.user_id,
            benefitType: b.benefit_type,
            benefitCategory: b.benefit_category,
            url: b.url,
            description: b.description,
            screenshotUrl: b.screenshot_url,
            claimedValue: b.claimed_value,
            approvedValue: b.approved_value,
            status: b.status,
            adminNotes: b.admin_notes,
            fraudFlagged: b.fraud_flagged,
            fraudReason: b.fraud_reason,
            submittedAt: b.submitted_at,
            reviewedAt: b.reviewed_at,
            reviewedBy: b.reviewed_by
        };
    },

    // Affiliate API
    getAffiliateDashboard: async (): Promise<import('../types').AffiliateDashboard> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/affiliate/dashboard`);
        if (!response.ok) throw new Error('Failed to fetch affiliate dashboard');
        const data = await response.json();
        return {
            tier: {
                id: data.tier.id,
                userId: data.tier.user_id,
                tierLevel: data.tier.tier_level,
                tierName: data.tier.tier_name,
                commissionRateL1: data.tier.commission_rate_l1,
                commissionRateL2: data.tier.commission_rate_l2,
                commissionRateL3: data.tier.commission_rate_l3,
                totalReferralsL1: data.tier.total_referrals_l1,
                totalReferralsL2: data.tier.total_referrals_l2,
                totalReferralsL3: data.tier.total_referrals_l3,
                totalEarnings: data.tier.total_earnings,
                pendingPayout: data.tier.pending_payout,
                lifetimePayout: data.tier.lifetime_payout,
                lastTierUpdate: data.tier.last_tier_update
            },
            relations: data.relations.map((r: any) => ({
                id: r.id,
                userId: r.user_id,
                referrerL1Id: r.referrer_l1_id,
                referrerL2Id: r.referrer_l2_id,
                referrerL3Id: r.referrer_l3_id
            })),
            referralLink: data.referral_link,
            totalReferrals: data.total_referrals,
            totalEarnings: data.total_earnings,
            pendingPayout: data.pending_payout,
            benefitBalance: data.benefit_balance
        };
    },

    getAffiliateTier: async (): Promise<import('../types').AffiliateTier> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/affiliate/tier`);
        if (!response.ok) throw new Error('Failed to fetch affiliate tier');
        const data = await response.json();
        return {
            id: data.id,
            userId: data.user_id,
            tierLevel: data.tier_level,
            tierName: data.tier_name,
            commissionRateL1: data.commission_rate_l1,
            commissionRateL2: data.commission_rate_l2,
            commissionRateL3: data.commission_rate_l3,
            totalReferralsL1: data.total_referrals_l1,
            totalReferralsL2: data.total_referrals_l2,
            totalReferralsL3: data.total_referrals_l3,
            totalEarnings: data.total_earnings,
            pendingPayout: data.pending_payout,
            lifetimePayout: data.lifetime_payout,
            lastTierUpdate: data.last_tier_update
        };
    },

    getAffiliateReferrals: async (tier: number = 1): Promise<any[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/affiliate/referrals?tier=${tier}`);
        if (!response.ok) return [];
        return await response.json();
    },

    // Payout API
    requestPayout: async (payout: {
        amount: number;
        method: string;
        payout_details: Record<string, any>;
    }): Promise<import('../types').PayoutRequest> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/affiliate/payouts/request`, {
            method: 'POST',
            body: JSON.stringify(payout)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || "Failed to request payout");
        }
        const p = await response.json();
        return {
            id: p.id,
            userId: p.user_id,
            amount: p.amount,
            method: p.method,
            payoutDetails: p.payout_details,
            status: p.status,
            adminNotes: p.admin_notes,
            requestedAt: p.requested_at,
            processedAt: p.processed_at,
            processedBy: p.processed_by,
            transactionHash: p.transaction_hash
        };
    },

    getMyPayouts: async (): Promise<import('../types').PayoutRequest[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/affiliate/payouts`);
        if (!response.ok) return [];
        const data = await response.json();
        return data.map((p: any) => ({
            id: p.id,
            userId: p.user_id,
            amount: p.amount,
            method: p.method,
            payoutDetails: p.payout_details,
            status: p.status,
            adminNotes: p.admin_notes,
            requestedAt: p.requested_at,
            processedAt: p.processed_at,
            processedBy: p.processed_by,
            transactionHash: p.transaction_hash
        }));
    },

    // Admin Benefits
    getPendingBenefits: async (): Promise<any[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/benefits/pending`);
        if (!response.ok) return [];
        return await response.json();
    },

    getAdminBenefitTypes: async (): Promise<any[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/benefit-types`);
        if (!response.ok) return [];
        return await response.json();
    },

    saveAdminBenefitType: async (data: any): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/benefit-types`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to create benefit type');
        }
        return await response.json();
    },

    updateAdminBenefitType: async (typeId: string, data: any): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/benefit-types/${typeId}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to update benefit type');
        }
        return await response.json();
    },

    getBenefitsHistory: async (status?: string): Promise<any[]> => {
        const url = status
            ? `${API_BASE_URL}/admin/benefits/history?status=${status}`
            : `${API_BASE_URL}/admin/benefits/history`;
        const response = await fetchWithAuth(url);
        if (!response.ok) return [];
        return await response.json();
    },

    approveBenefit: async (benefitId: string, approvedValue: number, adminNotes?: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/benefits/${benefitId}/approve`, {
            method: 'POST',
            body: JSON.stringify({ approved_value: approvedValue, status: 'approved', admin_notes: adminNotes })
        });
        if (!response.ok) throw new Error('Failed to approve benefit');
    },

    rejectBenefit: async (benefitId: string, adminNotes?: string, fraudFlagged: boolean = false, fraudReason?: string, sendEmail?: boolean): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/benefits/${benefitId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ status: 'rejected', admin_notes: adminNotes, fraud_flagged: fraudFlagged, fraud_reason: fraudReason, send_email: sendEmail })
        });
        if (!response.ok) throw new Error('Failed to reject benefit');
    },

    // Admin Payouts
    getPendingPayouts: async (): Promise<any[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/payouts/pending`);
        if (!response.ok) return [];
        return await response.json();
    },

    approvePayout: async (payoutId: string, adminNotes?: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/payouts/${payoutId}/approve`, {
            method: 'POST',
            body: JSON.stringify({ status: 'approved', admin_notes: adminNotes })
        });
        if (!response.ok) throw new Error('Failed to approve payout');
    },

    rejectPayout: async (payoutId: string, adminNotes?: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/payouts/${payoutId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ status: 'rejected', admin_notes: adminNotes })
        });
        if (!response.ok) throw new Error('Failed to reject payout');
    },

    markPayoutPaid: async (payoutId: string, transactionHash?: string, adminNotes?: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/payouts/${payoutId}/mark-paid`, {
            method: 'POST',
            body: JSON.stringify({ status: 'paid', transaction_hash: transactionHash, admin_notes: adminNotes })
        });
        if (!response.ok) throw new Error('Failed to mark payout as paid');
    },

    // Admin Affiliates
    getAllAffiliates: async (): Promise<any[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/affiliates`);
        if (!response.ok) return [];
        return await response.json();
    },

    updateAffiliateTier: async (userId: string, tierLevel: number): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/affiliates/${userId}/tier-update`, {
            method: 'POST',
            body: JSON.stringify({ tier_level: tierLevel })
        });
        if (!response.ok) throw new Error('Failed to update affiliate tier');
    },

    // Proxy Provider API
    getProxyConfig: async (): Promise<ProxyProvider | null> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/config`);
        if (response.status === 404) return null;
        if (!response.ok) throw new Error('Failed to fetch proxy config');
        const data = await response.json();
        return {
            id: data.id,
            name: data.name,
            providerType: data.provider_type,
            username: data.username,
            serviceName: data.service_name,
            proxyHost: data.proxy_host,
            httpPortStart: data.http_port_start,
            httpPortEnd: data.http_port_end,
            isActive: data.is_active,
            sessionLifetimeMinutes: data.session_lifetime_minutes,
            bandwidthLimitGb: data.bandwidth_limit_gb,
            bandwidthUsedGb: data.bandwidth_used_gb || 0,
            notificationEmail: data.notification_email,
            warnAt80: data.warn_at_80,
            warnAt50: data.warn_at_50,
            warnAt20: data.warn_at_20,
            lastSyncAt: data.last_sync_at,
            createdAt: data.created_at
        };
    },

    saveProxyConfig: async (config: ProxyProviderConfig): Promise<ProxyProvider> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/config`, {
            method: 'POST',
            body: JSON.stringify({
                username: config.username,
                password: config.password,
                service_name: config.serviceName,
                proxy_host: config.proxyHost,
                http_port: config.httpPort,
                session_lifetime_minutes: config.sessionLifetimeMinutes,
                bandwidth_limit_gb: config.bandwidthLimitGb,
                notification_email: config.notificationEmail,
                is_active: config.isActive,
                warn_at_80: config.warnAt80,
                warn_at_50: config.warnAt50,
                warn_at_20: config.warnAt20
            })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to save proxy config');
        }
        const data = await response.json();
        return {
            id: data.id,
            name: data.name,
            providerType: data.provider_type,
            username: data.username,
            serviceName: data.service_name,
            proxyHost: data.proxy_host,
            httpPortStart: data.http_port_start,
            httpPortEnd: data.http_port_end,
            isActive: data.is_active,
            sessionLifetimeMinutes: data.session_lifetime_minutes,
            bandwidthLimitGb: data.bandwidth_limit_gb,
            bandwidthUsedGb: data.bandwidth_used_gb || 0,
            notificationEmail: data.notification_email,
            warnAt80: data.warn_at_80,
            warnAt50: data.warn_at_50,
            warnAt20: data.warn_at_20,
            lastSyncAt: data.last_sync_at,
            createdAt: data.created_at
        };
    },

    testProxyConnection: async (): Promise<{ success: boolean; message: string; bandwidth_used_gb?: number }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/test`, {
            method: 'POST'
        });
        return await response.json();
    },

    syncProxyLocations: async (): Promise<{ success: boolean; locations_synced: number }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/sync-locations`, {
            method: 'POST'
        });
        return await response.json();
    },

    getProxyUsage: async (): Promise<ProxyUsageStats> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/usage`);
        return await response.json();
    },

    getGeoLocations: async (): Promise<GeoLocation[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/locations`);
        if (!response.ok) return [];
        const data = await response.json();
        return data.map((l: any) => ({
            countryCode: l.country_code,
            countryName: l.country_name,
            states: l.states || [],
            cities: l.cities || []
        }));
    },

    getGeoLocationsForCountry: async (countryCode: string): Promise<GeoLocation | null> => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/geo-locations?country_code=${countryCode}`);
            if (!response.ok) return null;
            const data = await response.json();
            if (data.length === 0) return null;
            const l = data[0];
            return {
                countryCode: l.country_code,
                countryName: l.country_name,
                states: l.states || [],
                cities: l.cities || []
            };
        } catch {
            return null;
        }
    },

    getProxySessions: async (activeOnly: boolean = true): Promise<ProxySession[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/sessions?active_only=${activeOnly}`);
        if (!response.ok) return [];
        const data = await response.json();
        return data.map((s: any) => ({
            id: s.id,
            sessionId: s.session_id,
            country: s.country,
            countryCode: s.country_code,
            state: s.state,
            city: s.city,
            ipAddress: s.ip_address,
            isActive: s.is_active,
            port: s.port,
            requestCount: s.request_count,
            validationSource: s.validation_source,
            validationStatus: s.validation_status,
            ga4Country: s.ga4_country,
            ga4City: s.ga4_city,
            oracleDecisionId: s.oracle_decision_id,
            createdAt: s.created_at,
            expiresAt: s.expires_at,
            lastUsedAt: s.last_used_at
        }));
    },

    releaseProxySession: async (sessionId: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/sessions/${sessionId}/release`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to release session');
    },

    releaseAllProxySessions: async (): Promise<{ success: boolean; sessions_released: number }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/sessions/release-all`, {
            method: 'POST'
        });
        return await response.json();
    },

    cleanupProxySessions: async (): Promise<{ success: boolean; message: string }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/cleanup`, {
            method: 'POST'
        });
        return await response.json();
    },

    testProxyTraffic: async (params: { countryCode?: string; countryName?: string; state?: string; city?: string }): Promise<{
        success: boolean;
        message?: string;
        session_id?: string;
        latency_ms?: number;
        detected?: {
            ip: string;
            country: string;
            country_code: string;
            region: string;
            city: string;
            isp: string;
            timezone: string;
            lat: number;
            lon: number;
        };
        requested?: {
            country: string;
            state?: string;
            city?: string;
        };
        geo_match?: boolean;
    }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/test-traffic`, {
            method: 'POST',
            body: JSON.stringify({
                country_code: params.countryCode,
                country_name: params.countryName,
                state: params.state,
                city: params.city
            })
        });
        return await response.json();
    },

    getProxyLogs: async (params?: { limit?: number; offset?: number; successOnly?: boolean; errorsOnly?: boolean }): Promise<ProxyLog[]> => {
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        if (params?.offset) queryParams.set('offset', params.offset.toString());
        if (params?.successOnly) queryParams.set('success_only', 'true');
        if (params?.errorsOnly) queryParams.set('errors_only', 'true');

        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/logs?${queryParams.toString()}`);
        if (!response.ok) return [];
        const data = await response.json();
        return data.map((l: any) => ({
            id: l.id,
            sessionId: l.session_id,
            projectId: l.project_id,
            requestUrl: l.request_url,
            responseCode: l.response_code,
            latencyMs: l.latency_ms,
            errorMessage: l.error_message,
            countryCode: l.country_code,
            state: l.state,
            city: l.city,
            ipAddress: l.ip_address,
            success: l.success,
            createdAt: l.created_at
        }));
    },

    getProxyLogStats: async (): Promise<ProxyLogStats> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/proxies/logs/stats`);
        const data = await response.json();
        return {
            total: data.total,
            successful: data.successful,
            failed: data.failed,
            successRate: data.success_rate,
            avgLatencyMs: data.avg_latency_ms
        };
    },

    getProjectsMissingGA: async (): Promise<{
        projects: Array<{
            id: string;
            name: string;
            status: string;
            userId: string;
            userEmail: string | null;
            userName: string | null;
            entryUrl: string;
            createdAt: string | null;
            dailyLimit: number;
            totalTarget: number;
            totalHits: number;
            tier: string | null;
            currentGaId?: string;
        }>;
        count: number;
    }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/missing-ga`);
        if (!response.ok) {
            throw new Error('Failed to fetch projects missing GA');
        }
        const data = await response.json();
        return {
            projects: data.projects.map((p: any) => ({
                id: p.id,
                name: p.name,
                status: p.status,
                userId: p.user_id,
                userEmail: p.user_email,
                userName: p.user_name,
                entryUrl: p.entry_url,
                createdAt: p.created_at,
                dailyLimit: p.daily_limit,
                totalTarget: p.total_target,
                totalHits: p.total_hits,
                tier: p.tier,
                currentGaId: p.current_ga_id
            })),
            count: data.count
        };
    },

    updateProjectGAId: async (projectId: string, gaId: string): Promise<{
        success: boolean;
        projectId: string;
        gaId: string;
        status: string;
        message: string;
    }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/projects/${projectId}/ga-id`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ gaId })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Failed to update GA ID');
        }
        const data = await response.json();
        return {
            success: data.success,
            projectId: data.project_id,
            gaId: data.ga_id,
            status: data.status,
            message: data.message
        };
    },

    // ─── Google Search Console ─────────────────────────────────────────

    getGscStatus: async (): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/status`);
        if (!response.ok) throw new Error('Failed to fetch GSC status');
        return response.json();
    },

    getGscTopQueries: async (days: number = 28, limit: number = 50): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/top-queries?days=${days}&limit=${limit}`);
        if (!response.ok) throw new Error('Failed to fetch GSC queries');
        return response.json();
    },

    getGscTopPages: async (days: number = 28, limit: number = 50): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/top-pages?days=${days}&limit=${limit}`);
        if (!response.ok) throw new Error('Failed to fetch GSC pages');
        return response.json();
    },

    getGscPerformanceTrend: async (days: number = 28): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/performance-trend?days=${days}`);
        if (!response.ok) throw new Error('Failed to fetch GSC trend');
        return response.json();
    },

    getGscCountries: async (days: number = 28, limit: number = 20): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/countries?days=${days}&limit=${limit}`);
        if (!response.ok) throw new Error('Failed to fetch GSC countries');
        return response.json();
    },

    getGscDevices: async (days: number = 28): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/devices?days=${days}`);
        if (!response.ok) throw new Error('Failed to fetch GSC devices');
        return response.json();
    },

    inspectGscUrl: async (url: string): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/inspect-url`, {
            method: 'POST',
            body: JSON.stringify({ url }),
        });
        if (!response.ok) throw new Error('Failed to inspect URL');
        return response.json();
    },

    getGscSitemaps: async (): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/sitemaps`);
        if (!response.ok) throw new Error('Failed to fetch GSC sitemaps');
        return response.json();
    },

    getGscIndexCoverage: async (days: number = 28): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/gsc/index-coverage-summary?days=${days}`);
        if (!response.ok) throw new Error('Failed to fetch index coverage');
        return response.json();
    },

    // ─── Roadmap & Changelog ─────────────────────────────────────────

    getRoadmapItems: async (sortBy: string = 'top_voted', status?: string): Promise<RoadmapItem[]> => {
        let url = `${API_BASE_URL}/api/roadmap?sort_by=${sortBy}`;
        if (status) url += `&status=${status}`;
        const response = await fetchWithAuth(url);
        if (!response.ok) throw new Error('Failed to fetch roadmap items');
        return response.json();
    },

    submitRoadmapItem: async (title: string, description: string): Promise<RoadmapItem> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/roadmap`, {
            method: 'POST',
            body: JSON.stringify({ title, description })
        });
        if (!response.ok) throw new Error('Failed to submit roadmap item');
        return response.json();
    },

    voteRoadmapItem: async (itemId: string, voteValue: number): Promise<{ message: string, new_score: number }> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/roadmap/${itemId}/vote`, {
            method: 'POST',
            body: JSON.stringify({ vote_value: voteValue })
        });
        if (!response.ok) throw new Error('Failed to vote');
        return response.json();
    },

    getChangelogItems: async (): Promise<ChangelogItem[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/changelog`);
        if (!response.ok) throw new Error('Failed to fetch changelog items');
        return response.json();
    },

    getAdminRoadmapItems: async (): Promise<RoadmapItemAdmin[]> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/roadmap`);
        if (!response.ok) throw new Error('Failed to fetch admin roadmap items');
        return response.json();
    },

    updateAdminRoadmapItem: async (itemId: string, updateData: Partial<RoadmapItemAdmin>): Promise<any> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/roadmap/${itemId}`, {
            method: 'PATCH',
            body: JSON.stringify(updateData)
        });
        if (!response.ok) throw new Error('Failed to update roadmap item');
        return response.json();
    },

    deleteAdminRoadmapItem: async (itemId: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/roadmap/${itemId}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Failed to delete roadmap item');
    },

    mergeAdminRoadmapItem: async (sourceId: string, targetId: string): Promise<void> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/admin/roadmap/${sourceId}/merge?target_id=${targetId}`, { method: 'POST' });
        if (!response.ok) throw new Error('Failed to merge roadmap item');
    }
};
